import React from 'react';
import clsx from 'clsx';
import {
  Form,
  Link,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
  useOutlet,
  useLocation
} from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import * as Headless from '@headlessui/react';
import {
  ArrowRightIcon,
  ArrowsUpDownIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PencilIcon,
  SparklesIcon,
  Squares2X2Icon,
  TrashIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { PageTitle } from '@oysterjs/uiv2/page';
import { Badge } from '@oysterjs/uiv2/badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import { Select } from '@oysterjs/uiv2/select';
import { MultiListbox, MultiListboxOption } from '@oysterjs/uiv2/multiselect';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/uiv2/fieldset';
import { AnimateIn } from '@oysterjs/uiv2/transition';

import { client } from '../../api/client';
import {
  AddCaraWorkspaceMessageDocument,
  AddCaraWorkspaceMessageMutation,
  Address,
  AgencyUserPreferencesInput,
  CaraWorkspaceArtifactType,
  CaraWorkspaceFieldsFragment,
  CaraWorkspaceMessageRole,
  CaraWorkspaceOverviewFieldsFragment,
  CaraWorkspacePlanStageEventType,
  CaraWorkspacePlanStageState,
  CaraWorkspacePlanStageType,
  CaraWorkspacePreset,
  CaraWorkspaceSchemaVersion,
  CaraWorkspaceState,
  CaraWorkspaceTool,
  CreateCaraWorkspaceDocument,
  CreateCaraWorkspaceMutation,
  CustomPresetFieldsFragment,
  DocumentType,
  DraftDocumentToolInput,
  DraftEmailToolInput,
  FillTemplateToolFieldsFragment,
  GetCaraWorkspaceDocument,
  GetCaraWorkspaceQuery,
  HtmlDocumentTemplateFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  ResolveServiceRequestDocument,
  ResolveServiceRequestInput,
  ResolveServiceRequestMutation,
  ResolveServiceRequestMutationVariables,
  ServiceRequestState,
  UpdateCaraWorkspacesDocument,
  UpdateCaraWorkspacesMutation,
  UpdateCaraWorkspacesMutationVariables,
  WatchCaraWorkspaceDocument,
  WatchCaraWorkspaceSubscription,
  WatchCaraWorkspaceSubscriptionVariables
} from '../../api/graphql';
import { ApolloError, useSubscription } from '@apollo/client';
import { Await, TransitionDiv } from '@oysterjs/uiv2/async';
import { Button } from '@oysterjs/uiv2/button';
import { ResolveServiceRequestDialog } from '../servicing';
import {
  DialogTitle,
  DialogDescription,
  Dialog,
  DialogBody,
  DialogActions,
  MountableDialog
} from '@oysterjs/uiv2/dialog';
import { Textarea } from '@oysterjs/uiv2/textarea';
import { FileInput } from '@oysterjs/uiv2/file';
import { remark } from 'remark';
import stripMarkdown from 'strip-markdown';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/uiv2/checkbox';
import { getStaticsBaseUrl } from '@oysterjs/corev2/config';

const getFilesFromEvent = (dataTransfer?: DataTransfer): File[] => {
  const files: File[] = [];

  if (dataTransfer && dataTransfer.items) {
    // Use DataTransferItemList interface to access the file(s)
    for (let i = 0; i < dataTransfer.items.length; i++) {
      // If dropped items aren't files, reject them
      if (dataTransfer.items[i].kind === 'file') {
        const file = dataTransfer.items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
  } else if (dataTransfer && dataTransfer.files) {
    // Use DataTransfer interface to access the file(s)
    for (let i = 0; i < dataTransfer.files.length || 0; i++) {
      const file = dataTransfer.files[i];
      if (file) {
        files.push(file);
      }
    }
  }

  return files;
};

const CaraInput = (props: {
  onSubmit: (message: string, attachments: File[]) => Promise<void>;
  inline?: boolean;
  loading?: boolean;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const [submitLoading, setSubmitLoading] = React.useState(false);
  const loading = props.loading || submitLoading;

  const [, setDragging] = React.useState(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);

  const ref = React.useRef<HTMLInputElement>(null);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const addFiles = (files: File[]) =>
    setAttachments((prev) => {
      const newAttachments = [...prev, ...files];
      return newAttachments;
    });

  const removeFile = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setAttachments((prev) => {
      const newAttachments = prev.filter((_, i) => i !== index);
      return newAttachments;
    });
  };

  const onDragEnter = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);
  };

  const onDragOver = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const onDrop = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);

    const files = getFilesFromEvent(e.dataTransfer);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await props.onSubmit(query.trim(), attachments);
      setQuery('');
      setAttachments([]);
      if (textAreaRef.current) {
        textAreaRef.current.blur();
        setTimeout(() => {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }, 200);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <motion.div
      className={clsx(
        'bg-white p-6 w-full mt-8',
        props.inline
          ? 'shadow-chat-inline transition-all duration-300 rounded-2xl ring-1 ring-neutral-200'
          : 'shadow transition-all duration-300 rounded-md',
        isFocused && !props.inline && 'shadow-lg',
        isFocused && props.inline && 'shadow-chat-inline-lg'
      )}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <Headless.Textarea
        ref={textAreaRef}
        placeholder={props.inline ? 'Follow up...' : 'Ask Cara...'}
        className={clsx(
          'p-0 m-0',
          'font-body peer text-sm w-full',
          'border-0 resize-none outline-none ring-none',
          'focus:outline-0 focus:ring-0 focus:border-0'
        )}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        rows={props.inline && !isFocused && !query ? 1 : 6}
      />
      <Headless.Input
        ref={ref}
        type="file"
        onChange={(e) => addFiles(Array.from(e.currentTarget?.files || []))}
        multiple
        className="hidden"
      />
      <AnimatePresence mode="wait">
        {(query?.length >= 1 || attachments.length >= 1) && (
          <motion.div
            style={{ overflow: 'hidden' }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{
              height: { duration: 0.15, ease: 'anticipate' },
              opacity: { duration: 0.3, ease: 'anticipate' }
            }}
            exit={{ height: 0, opacity: 0 }}
            key="container"
          >
            {attachments.length >= 1 && (
              <div className="mt-2 mb-4 flex flex-wrap flex-row gap-2">
                {attachments.map((attachment, i) => (
                  <div
                    key={attachment.name}
                    className="rounded-full bg-neutral-100 px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1"
                  >
                    <PaperClipIcon className="size-3 text-neutral-500" />
                    <p className="text-sm text-neutral-500">{attachment.name}</p>
                    <Headless.Button type="button" onClick={(e) => removeFile(e, i)}>
                      <XMarkIcon className="size-3" />
                    </Headless.Button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <div className="flex justify-between">
                <Headless.Button
                  className="text-neutral-500 border border-neutral-200 rounded-full p-2 hover:bg-neutral-100 hover:text-neutral-700 data-[disabled]:opacity-50 data-[disabled]:hover:bg-white transition-all duration-150"
                  disabled={loading}
                  onClick={() => ref?.current?.click()}
                >
                  <PaperClipIcon className="w-4 h-4" />
                </Headless.Button>
                <Headless.Button
                  className="text-white border border-primary-500 rounded-full p-2 bg-primary-500 hover:bg-primary-500 data-[disabled]:opacity-50 transition-all duration-150"
                  disabled={loading || query.trim().length === 0}
                  onClick={onSubmit}
                >
                  {!loading && <PaperAirplaneIcon className="w-4 h-4" />}
                  {loading && <Spinner size="size-4" />}
                </Headless.Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const presets = [
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-primary-100 text-primary-700">
        <DocumentCheckIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.CertificateReview,
    title: 'Certificate Review',
    description: 'Review a certificate for correctness, discrepancies, and E&O exposure'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-primary-100 text-primary-700">
        <DocumentCheckIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.ContractReview,
    title: 'Contract Review',
    description:
      'Review a contract for insurance requirements and compare to quote or policy details'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-indigo-100 text-indigo-700">
        <MagnifyingGlassIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.CoverageComparison,
    title: 'Coverage Comparison',
    description: 'Compare coverage details between multiple quote or policy options'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-indigo-100 text-indigo-700">
        <MagnifyingGlassIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.DocumentExploration,
    title: 'Explore a Policy',
    description: 'Ask Cara to answer questions about a policy, quote, binder, or other document'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-primary-100 text-primary-700">
        <DocumentCheckIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.UnderwritingAnalysis,
    title: 'Enrich and Verify Application',
    description: 'Enrich and verify commercial applicant data from all public web sources'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-emerald-100 text-emerald-700">
        <DocumentDuplicateIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Fill Forms and Certificates',
    description: 'Cross-fill ACORD forms and certificates from other documents and instructions'
  },
  {
    icon: (
      <div className="inline-flex p-2 rounded bg-emerald-100 text-emerald-700">
        <DocumentDuplicateIcon className="size-4" />
      </div>
    ),
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Create Quote Proposal',
    description: 'Create a quote proposal for a customer from one or more quote documents'
  }
];

export const CopilotStartPage = (props: { agencyUser: LoggedInAgencyUserFieldsFragment }) => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();

  const [taskDialogShowing, setTaskDialogShowing] = React.useState(false);
  const [task, setTask] = React.useState<{
    preset: CaraWorkspacePreset;
    title: string;
    description: string;
  } | null>(null);

  const createWorkspace = async (
    query: string,
    attachments: File[],
    documentIds?: DocumentType[],
    presetOverride?: CaraWorkspacePreset
  ) => {
    const res = await client.mutate<CreateCaraWorkspaceMutation>({
      mutation: CreateCaraWorkspaceDocument,
      variables: {
        input: {
          message: query.trim(),
          files: attachments,
          preset: presetOverride || task?.preset || undefined,
          documentIds: documentIds || []
        }
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    const id = res.data?.createCaraWorkspace?.id;
    if (!id) {
      throw new Error('Failed to create workspace');
    }

    navigate(`/app/workspaces/${id}`);
  };

  return (
    <div className="py-16">
      <h1 className="m-0 text-center text-xl font-medium">What can Cara do for you?</h1>
      <p className="text-sm mt-2 text-neutral-500 text-pretty max-w-md text-center mx-auto">
        Ask Cara to help with anything you need, from finding information to drafting emails and
        documents.
      </p>
      <div className="mt-8 mx-auto w-full max-w-4xl">
        <div className="w-full text-right">
          <Headless.Button onClick={() => navigate('/app/home/preferences')}>
            <Cog6ToothIcon className="size-4 text-neutral-500 hover:text-neutral-700 transition-all duration-150" />
          </Headless.Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 justify-between">
          {presets
            .filter(
              (p) =>
                props.agencyUser.preferences.favoritePresets?.presets?.includes(p.preset) ?? true
            )
            .map((task) => (
              <Headless.Button
                key={task.preset}
                title={task.description}
                onClick={() => {
                  setTask(task);
                  setTaskDialogShowing(true);
                }}
              >
                <div className="text-left p-6 rounded-lg bg-white shadow hover:shadow-xl transition-all duration-300 w-full h-full">
                  <div className="mb-2 hidden sm:block">{task.icon}</div>
                  <h2 className="text-sm/6 font-body m-0 p-0 font-medium flex flex-row items-center gap-1.5">
                    <span>{task.title}</span>
                    <ArrowRightIcon className="size-3" />
                  </h2>
                  <p className="text-sm text-neutral-500">{task.description}</p>
                </div>
              </Headless.Button>
            ))}
          {props.agencyUser.preferences.favoritePresets?.customPresets.map((preset) => (
            <Headless.Button
              key={preset.id}
              title={preset.title}
              onClick={() => navigate(`/app/home/presets/${preset.id}`)}
            >
              <div className="text-left p-6 rounded-lg bg-white shadow hover:shadow-xl transition-all duration-300 w-full h-full">
                <div className="mb-2 hidden sm:block">
                  <div className="inline-flex p-2 rounded bg-neutral-100 text-neutral-700">
                    <SparklesIcon className="size-4" />
                  </div>
                </div>
                <h2 className="text-sm/6 font-body m-0 p-0 font-medium flex flex-row items-center gap-1.5">
                  <span>{preset.title}</span>
                  <ArrowRightIcon className="size-3" />
                </h2>
                <p className="text-sm text-neutral-500">{preset.description}</p>
              </div>
            </Headless.Button>
          ))}
        </div>
      </div>
      <div className="max-w-2xl mx-auto">
        <CaraInput onSubmit={createWorkspace} />
      </div>
      <Dialog open={taskDialogShowing} onClose={() => setTaskDialogShowing(false)} size="xl">
        <DialogTitle>{task?.title}</DialogTitle>
        <DialogDescription>{task?.description}</DialogDescription>

        {task?.preset === CaraWorkspacePreset.CertificateReview && (
          <CopilotCertificateReviewForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.ContractReview && (
          <CopilotContractReviewForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.CoverageComparison && (
          <CopilotCoverageComparisonForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.UnderwritingAnalysis && (
          <CopilotUnderwritingAnalysisForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance && (
          <CopilotGenerateCertificateOfLiabilityInsuranceForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.GenerateTemplate && (
          <CopilotGenerateTemplateForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.DocumentExploration && (
          <CopilotDocumentExplorationForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
        {task?.preset === CaraWorkspacePreset.GenerateQuoteProposal && (
          <CopilotGenerateQuoteProposalForm
            onSubmit={createWorkspace}
            onClose={() => setTaskDialogShowing(false)}
          />
        )}
      </Dialog>
      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </div>
  );
};

const CopilotCertificateReviewForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [certificateFiles, setCertificateFiles] = React.useState<File[]>([]);
  const [quoteFiles, setQuoteFiles] = React.useState<File[]>([]);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(message, [
      ...certificateFiles.map(
        (f) =>
          new File([f], '[Certificate] ' + f.name, { type: f.type, lastModified: f.lastModified })
      ),
      ...quoteFiles.map(
        (f) =>
          new File([f], '[Quote or Policy] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    ]);
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Certificate of Insurance</Label>
              <Description>
                Upload the certificate of insurance you want Cara to review.
              </Description>
              <FileInput multiple onChange={(files) => setCertificateFiles(files)} />
            </Field>
            <Field>
              <Label>Quotes or Policies</Label>
              <Description>
                Upload any quotes or policies you want Cara to compare to the certificate of
                insurance.
              </Description>
              <FileInput multiple onChange={(files) => setQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when reviewing the certificate of
                insurance.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotContractReviewForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [contractFiles, setContractFiles] = React.useState<File[]>([]);
  const [quoteFiles, setQuoteFiles] = React.useState<File[]>([]);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(message, [
      ...contractFiles.map(
        (f) => new File([f], '[Contract] ' + f.name, { type: f.type, lastModified: f.lastModified })
      ),
      ...quoteFiles.map(
        (f) =>
          new File([f], '[Quote or Policy] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    ]);
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Contract</Label>
              <Description>
                Upload the contract you want Cara to review for insurance requirements.
              </Description>
              <FileInput multiple onChange={(files) => setContractFiles(files)} />
            </Field>
            <Field>
              <Label>Quotes or Policies</Label>
              <Description>
                Upload any quotes or policies you want Cara to compare to the contract requirements.
              </Description>
              <FileInput multiple onChange={(files) => setQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when reviewing the contract.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotCoverageComparisonForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [firstQuoteFiles, setFirstQuoteFiles] = React.useState<File[]>([]);
  const [secondQuoteFiles, setSecondQuoteFiles] = React.useState<File[]>([]);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(message, [
      ...firstQuoteFiles.map(
        (f) =>
          new File([f], '[Option 1] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      ),
      ...secondQuoteFiles.map(
        (f) =>
          new File([f], '[Option 2] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    ]);
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>First Option</Label>
              <Description>
                Upload any quotes or policies you want Cara that comprise the first option to
                compare.
              </Description>
              <FileInput multiple onChange={(files) => setFirstQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Second Option</Label>
              <Description>
                Upload any quotes or policies you want Cara to compare to the first option.
              </Description>
              <FileInput multiple onChange={(files) => setSecondQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes you want Cara to consider when comparing the quotes or
                policies.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotGenerateCertificateOfLiabilityInsuranceForm = (props: {
  onSubmit: (
    query: string,
    attachments: File[],
    documentIds?: DocumentType[],
    presetOverride?: CaraWorkspacePreset
  ) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [quoteOrPolicyFiles, setQuoteOrPolicyFiles] = React.useState<File[]>([]);
  const [presetOverride, setPresetOverride] = React.useState<
    | CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance
    | CaraWorkspacePreset.GenerateEvidenceOfPropertyInsurance
  >(CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(
      message,
      [
        ...quoteOrPolicyFiles.map(
          (f) =>
            new File([f], '[Binder or Policy] ' + f.name, {
              type: f.type,
              lastModified: f.lastModified
            })
        )
      ],
      undefined,
      presetOverride
    );
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Certificate Type</Label>
              <Description>Choose the type of certificate you want Cara to generate.</Description>
              <Select
                value={presetOverride}
                onChange={(e) =>
                  setPresetOverride(
                    e.currentTarget.value as
                      | CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance
                      | CaraWorkspacePreset.GenerateEvidenceOfPropertyInsurance
                  )
                }
              >
                <option value={CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance}>
                  Certificate of Liability Insurance (ACORD 25)
                </option>
                <option value={CaraWorkspacePreset.GenerateEvidenceOfPropertyInsurance}>
                  Evidence of Property Insurance (ACORD 27)
                </option>
              </Select>
            </Field>
            <Field>
              <Label>Binders or Policies</Label>
              <Description>
                {presetOverride === CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance
                  ? 'Upload the binders or policies you want Cara to use while generating a certificate of liability insurance.'
                  : 'Upload the binders or policies you want Cara to use while generating the evidence of property insurance.'}
              </Description>
              <FileInput multiple onChange={(files) => setQuoteOrPolicyFiles(files)} />
            </Field>
            <Field>
              <Label>Additional Information</Label>
              <Description>
                {presetOverride === CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance
                  ? "Add any additional information you want Cara to consider when generating a certificate of liability insurance, such as the certificate holder's name and address, the description of operations, or any other requirements."
                  : "Add any additional information you want Cara to consider when generating the evidence of property insurance, such as the additional interest's name and address, the description of the property, or any other requirements."}
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={4}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotGenerateTemplateForm = (props: {
  onSubmit: (query: string, attachments: File[], documentIds: DocumentType[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<File[]>([]);
  const [documentIds, setDocumentIds] = React.useState<DocumentType[]>([]);
  const [certificateHolder, setCertificateHolder] = React.useState('');
  const [additionalInterest, setAdditionalInterest] = React.useState('');

  const onSubmit = async () => {
    setLoading(true);
    let finalMessage = '';
    if (message.trim()) {
      finalMessage += `\n\n${message.trim()}\n\n`;
    }
    if (
      documentIds.includes(DocumentType.Acord_25) ||
      documentIds.includes(DocumentType.Acord_24)
    ) {
      finalMessage += `**Certificate Holder**\n\n${certificateHolder.trim() || "Use the named insured's information as the certificate holder."}\n\n`;
    }
    if (
      documentIds.includes(DocumentType.Acord_27) ||
      documentIds.includes(DocumentType.Acord_28)
    ) {
      finalMessage += `**Additional Interest**\n\n${additionalInterest.trim() || 'Reference the documentation accordingly.'}\n\n`;
    }

    await props.onSubmit(
      finalMessage.trim(),
      [
        ...inputFiles.map(
          (f) =>
            new File([f], '[Input] ' + f.name, {
              type: f.type,
              lastModified: f.lastModified
            })
        )
      ],
      documentIds
    );
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Forms and Certificates</Label>
              <Description>
                Choose the forms and certificates you'd like Cara to fill out.
              </Description>
              <MultiListbox
                onChange={(value: DocumentType[]) => setDocumentIds(value)}
                value={documentIds}
                placeholder="Select templates"
              >
                <MultiListboxOption value={DocumentType.Acord_24}>
                  ACORD 24 (Certificate of Property Insurance)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_25}>
                  ACORD 25 (Certificate of Liability Insurance)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_27}>
                  ACORD 27 (Evidence of Property Insurance)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_28}>
                  ACORD 28 (Evidence of Commercial Property Insurance)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_35}>
                  ACORD 35 (Lost Policy Release / Cancellation Request)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_36}>
                  ACORD 36 (Agent / Broker Change of Record)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_75}>
                  ACORD 75 (Insurance Binder)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_125}>
                  ACORD 125 (Commercial Insurance Application)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_126}>
                  ACORD 126 (Commercial General Liability)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_130}>
                  ACORD 130 (Workers' Compensation)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_131}>
                  ACORD 131 (Excess/Umbrella Liability)
                </MultiListboxOption>
                <MultiListboxOption value={DocumentType.Acord_140}>
                  ACORD 140 (Commercial Property)
                </MultiListboxOption>
              </MultiListbox>
            </Field>
            <Field>
              <Label>Input Files</Label>
              <Description>
                Any files you'd like Cara to parse and use as input to cross-file into templates.
              </Description>
              <FileInput multiple onChange={(files) => setInputFiles(files)} />
            </Field>
            {(documentIds.includes(DocumentType.Acord_24) ||
              documentIds.includes(DocumentType.Acord_25)) && (
              <Field>
                <Label>Certificate Holder</Label>
                <Description>
                  Name and address of the certificate holder (if any). If not provided, Cara will
                  use the named insured's information as the certificate holder.
                </Description>
                <Textarea
                  value={certificateHolder}
                  onChange={(e) => setCertificateHolder(e.currentTarget.value)}
                  rows={3}
                />
              </Field>
            )}
            {(documentIds.includes(DocumentType.Acord_27) ||
              documentIds.includes(DocumentType.Acord_28)) && (
              <Field>
                <Label>Additional Interest</Label>
                <Description>
                  Name and address of the additional interest (if any). If not provided, Cara will
                  reference the documentation accordingly.
                </Description>
                <Textarea
                  value={additionalInterest}
                  onChange={(e) => setAdditionalInterest(e.currentTarget.value)}
                  rows={3}
                />
              </Field>
            )}
            <Field>
              <Label>Additional Information</Label>
              <Description>
                Add any additional information you want Cara to consider when filling out the
                template. This can be specific instructions or other free-form information that
                should be parsed and mapped to template fields
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={5}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotDocumentExplorationForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<File[]>([]);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(message, [
      ...inputFiles.map(
        (f) =>
          new File([f], '[Policy] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    ]);
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Input Files</Label>
              <Description>Files you'd like Cara to use as context to answer questions</Description>
              <FileInput multiple onChange={(files) => setInputFiles(files)} />
            </Field>
            <Field>
              <Label>Question or Prompt</Label>
              <Description>
                Any question or prompt you'd like Cara to answer about the documents.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={5}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotGenerateQuoteProposalForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [firstQuoteFiles, setFirstQuoteFiles] = React.useState<File[]>([]);
  const [secondQuoteFiles, setSecondQuoteFiles] = React.useState<File[]>([]);

  const onSubmit = async () => {
    setLoading(true);
    await props.onSubmit(message, [
      ...firstQuoteFiles.map(
        (f) =>
          new File([f], '[Option 1] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      ),
      ...secondQuoteFiles.map(
        (f) =>
          new File([f], '[Option 2] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    ]);
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>First Option</Label>
              <Description>
                Upload any quotes or binders you want Cara to use as part of the first option to
                present in the quote proposal.
              </Description>
              <FileInput multiple onChange={(files) => setFirstQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Second Option</Label>
              <Description>
                Upload any quotes or binders you want Cara to use as part of the second option to
                present in the quote proposal, if any.
              </Description>
              <FileInput multiple onChange={(files) => setSecondQuoteFiles(files)} />
            </Field>
            <Field>
              <Label>Additional Instructions</Label>
              <Description>
                Add any additional instructions you want Cara to consider when comparing the quote
                options and creating the quote proposal.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

const CopilotUnderwritingAnalysisForm = (props: {
  onSubmit: (query: string, attachments: File[]) => Promise<void>;
  onClose: () => void;
}) => {
  // DO NOT CHANGE THESE WITHOUT CHANGING THE BACKEND
  const validLinesOfBusiness = [
    {
      value: 'Business Operations',
      displayValue: 'Business Operations (including Liability Coverage)'
    },
    { value: 'Workers Compensation', displayValue: 'Workers Compensation' },
    { value: 'Property', displayValue: 'Property Coverage' },
    { value: 'Auto', displayValue: 'Auto Coverage' }
  ];

  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<File[]>([]);
  const [linesOfBusiness, setLinesOfBusiness] = React.useState<string[]>(['Business Operations']);

  const onSubmit = async () => {
    setLoading(true);

    await props.onSubmit(
      // DO NOT CHANGE THIS MESSAGE FORMAT WITHOUT CHANGING THE BACKEND
      `${message}\n\nCoverage Information: ${linesOfBusiness.join(', ')}`,
      inputFiles.map(
        (f) =>
          new File([f], '[Input Document] ' + f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    );
    setLoading(false);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Input Documents</Label>
              <Description>
                Upload any documents you want Cara to analyze for commercial underwriting purposes.
              </Description>
              <FileInput multiple onChange={(files) => setInputFiles(files)} />
            </Field>
            <Field>
              <Label>Coverage Review</Label>
              <Description>
                Select the lines of business you would like reviewed for this application.
              </Description>
              <CheckboxGroup>
                {validLinesOfBusiness.map(({ value, displayValue }) => (
                  <CheckboxField>
                    <Checkbox
                      color="sky"
                      checked={linesOfBusiness.includes(value)}
                      disabled={value === 'Business Operations'}
                      onChange={(v) =>
                        setLinesOfBusiness((prev) =>
                          v ? [...prev, value] : prev.filter((p) => p !== value)
                        )
                      }
                    />
                    <Label>{displayValue}</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
            </Field>
            <Field>
              <Label>Additional Notes</Label>
              <Description>
                Add any additional notes or specific requirements you want Cara to consider during
                the analysis.
              </Description>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                rows={3}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
          Close
        </Button>
        <Button type="submit" color="sky" loading={loading}>
          Create Workspace
        </Button>
      </DialogActions>
    </Form>
  );
};

export const AgencyUserPreferencesPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          agencyUser: LoggedInAgencyUserFieldsFragment;
          presets?: CustomPresetFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) =>
          data?.agencyUser &&
          data?.presets && (
            <AgencyUserPreferencesDialog presets={data.presets} agencyUser={data.agencyUser} />
          )
        }
      </Await>
    </React.Suspense>
  );
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const AgencyUserPreferencesDialog = (props: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
  presets: CustomPresetFieldsFragment[];
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();

  const [selectedTab, setSelectedTab] = React.useState<'BUILTIN' | 'CUSTOM'>('BUILTIN');

  const [, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<AgencyUserPreferencesInput>({
    favoritePresets: {
      customPresetIds: props.agencyUser.preferences.favoritePresets.customPresets.map((c) => c.id),
      presets: props.agencyUser.preferences.favoritePresets.presets
    }
  });
  const { updateForm } = useFormActions(setForm, setValidationErrors);

  const updateFormAndRefresh = (
    fieldParts: string[],
    updateFn: (prev: AgencyUserPreferencesInput) => AgencyUserPreferencesInput
  ) =>
    updateForm(fieldParts, (prev) => {
      const updated = updateFn(prev);
      submit(updated, { method: 'PUT', encType: 'application/json' });
      return updated;
    });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(form, { method: 'PUT', encType: 'application/json' });
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="xl">
      <DialogTitle>Customize presets</DialogTitle>
      <DialogDescription>
        Choose which presets you'd like to be able to access directly from the home page.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          {/* tabs */}
          <div className="mb-6">
            <div className="grid grid-cols-1 sm:hidden">
              {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
              <select
                defaultValue="BUILTIN"
                aria-label="Select a tab"
                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-neutral-900 outline outline-1 -outline-offset-1 outline-neutral-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                onChange={(e) => setSelectedTab(e.currentTarget.value as 'BUILTIN' | 'CUSTOM')}
              >
                <option value="BUILTIN">Builtin Presets</option>
                {!!props.presets?.length && <option value="CUSTOM">Custom Presets</option>}
              </select>
              <ChevronDownIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-neutral-500"
              />
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-neutral-200">
                <nav aria-label="Tabs" className="-mb-px flex">
                  <Headless.Button
                    onClick={() => setSelectedTab('BUILTIN')}
                    className={classNames(
                      selectedTab === 'BUILTIN'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
                      'w-1/4 border-b-2 px-1 py-4 text-center text-sm font-medium'
                    )}
                  >
                    Builtin Presets
                  </Headless.Button>
                  {!!props.presets?.length && (
                    <Headless.Button
                      onClick={() => setSelectedTab('CUSTOM')}
                      className={classNames(
                        selectedTab === 'CUSTOM'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
                        'w-1/4 border-b-2 px-1 py-4 text-center text-sm font-medium'
                      )}
                    >
                      Custom Presets
                    </Headless.Button>
                  )}
                </nav>
              </div>
            </div>
          </div>

          {selectedTab === 'CUSTOM' && (
            <Fieldset>
              <CheckboxGroup>
                {props.presets.map((preset) => (
                  <CheckboxField key={preset.id}>
                    <Checkbox
                      color="sky"
                      checked={!!form.favoritePresets?.customPresetIds.find((p) => p === preset.id)}
                      disabled={navigation.state === 'submitting'}
                      onChange={(checked) =>
                        updateFormAndRefresh(['favoritePresets', 'customPresetIds'], (prev) => ({
                          ...prev,
                          favoritePresets: {
                            ...(prev.favoritePresets || {}),
                            presets: prev.favoritePresets?.presets || [],
                            customPresetIds: checked
                              ? [...(prev.favoritePresets?.customPresetIds || []), preset.id]
                              : prev.favoritePresets?.customPresetIds.filter(
                                  (p) => p !== preset.id
                                ) || []
                          }
                        }))
                      }
                    />
                    <Label>{preset.title}</Label>
                    <Description>{preset.description}</Description>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
            </Fieldset>
          )}

          {selectedTab === 'BUILTIN' && (
            <Fieldset>
              <CheckboxGroup>
                {presets.map((preset) => (
                  <CheckboxField key={preset.preset}>
                    <Checkbox
                      color="sky"
                      checked={!!form.favoritePresets?.presets.find((p) => p === preset.preset)}
                      disabled={navigation.state === 'submitting'}
                      onChange={(checked) =>
                        updateFormAndRefresh(['favoritePresets', 'presets'], (prev) => ({
                          ...prev,
                          favoritePresets: {
                            ...(prev.favoritePresets || {}),
                            presets: checked
                              ? [...(prev.favoritePresets?.presets || []), preset.preset]
                              : prev.favoritePresets?.presets.filter((p) => p !== preset.preset) ||
                                [],
                            customPresetIds: prev.favoritePresets?.customPresetIds || []
                          }
                        }))
                      }
                    />
                    <Label>{preset.title}</Label>
                    <Description>{preset.description}</Description>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
            </Fieldset>
          )}
        </DialogBody>
      </Form>
    </MountableDialog>
  );
};

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0]?.toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const titleCaseFromCamelCase = (s: string) =>
  s
    // Handle consecutive uppercase letters (acronyms)
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // Handle camelCase transitions
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Handle numbers
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    // Split and capitalize
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();

const uniq = <T,>(arr: T[]) => [...new Set(arr)];

const getWorkspaceStateTitle = (state: CaraWorkspaceState) => {
  switch (state) {
    case CaraWorkspaceState.Created:
      return 'Created';
    case CaraWorkspaceState.Idle:
      return 'Idle';
    case CaraWorkspaceState.WaitingForInput:
      return 'Waiting';
    case CaraWorkspaceState.Working:
      return 'Working';
  }
};

const WorkspaceStateBadge = (props: { state: CaraWorkspaceState }) => {
  const title = getWorkspaceStateTitle(props.state);

  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [CaraWorkspaceState.Created]: 'neutral',
          [CaraWorkspaceState.Idle]: 'green',
          [CaraWorkspaceState.WaitingForInput]: 'amber',
          [CaraWorkspaceState.Working]: 'blue'
        }[props.state] || 'neutral'
      }
    >
      {title}
    </Badge>
  );
};
const FilterButton = <T,>(props: {
  label: string;
  multiple?: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
  values: { label: string; value: T }[];
  initialValues?: T[];
  onChange: (values: T[]) => void;
}) => {
  const [selected, setSelected] = React.useState(props.initialValues || []);
  const anySelected = selected.length > 0;
  const selectedCount = selected.length;

  React.useEffect(() => {
    props.onChange(selected);
  }, [selected]);

  return (
    <Headless.Menu
      as="div"
      className={clsx(
        'relative',
        'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300'
      )}
    >
      <Headless.MenuButton
        className={clsx(
          'inline-flex w-full items-center justify-center gap-x-1 rounded-full px-3 py-1.5',
          'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300',
          !anySelected &&
            'text-neutral-700 bg-white/50 border border-neutral-300 border-dashed hover:bg-neutral-50/50',
          anySelected && 'text-blue-600/80 bg-blue-50 border border-blue-600/80'
        )}
      >
        <div>
          {!anySelected && (
            <props.icon aria-hidden="true" className="h-3.5 w-3.5 text-neutral-500" />
          )}
          {anySelected && (
            <props.icon aria-hidden="true" className="h-3.5 w-3.5 text-blue-600/80" />
          )}
        </div>
        <div className="align-middle text-xs font-medium">{props.label}</div>
        {selectedCount > 0 && (
          <div className="text-xs font-medium rounded-md ml-1 px-1.5 bg-blue-600/80 text-white">
            {selectedCount}
          </div>
        )}
      </Headless.MenuButton>
      <Headless.MenuItems
        transition
        className="absolute left-0 z-10 mt-2 px-2 py-2 flex flex-col gap-y-0.5 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in w-auto min-w-max"
      >
        {props.values.map(({ label, value }) => (
          <Headless.MenuItem
            as="div"
            className={clsx(
              'flex flex-grow rounded-md cursor-pointer px-3.5 py-1.5 text-sm data-[focus]:outline-none',
              !selected.includes(value) &&
                'text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900',
              selected.includes(value) &&
                'font-medium text-blue-600/80 bg-blue-50 data-[focus]:bg-blue-50 data-[focus]:text-blue-600/80'
            )}
            onClick={(e) => {
              e.preventDefault();
              setSelected((prev) =>
                props.multiple
                  ? prev.includes(value)
                    ? prev.filter((v) => v !== value)
                    : [...prev, value]
                  : prev.includes(value)
                    ? []
                    : [value]
              );
            }}
          >
            <div className="flex flex-row items-center justify-between gap-x-8 w-full">
              <div className="">{label}</div>
              <div className={clsx(selected.includes(value) ? 'visible' : 'invisible')}>
                <CheckIcon className="size-4" />
              </div>
            </div>
          </Headless.MenuItem>
        ))}
      </Headless.MenuItems>
    </Headless.Menu>
  );
};

export const CopilotWorkspacesPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ caraWorkspaces?: CaraWorkspaceOverviewFieldsFragment[] }> }
    | undefined;

  const [loading, setLoading] = React.useState(false);
  const submit = useSubmit();
  const navigation = useNavigation();

  const defaultView = JSON.parse(
    window.localStorage.getItem('cara_workspaces_default_view') || '{}'
  );

  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<CaraWorkspaceState[]>(
    defaultView.statusFilter || []
  );
  const [userFilter, setUserFilter] = React.useState<string[]>(defaultView.userFilter || []);
  const [dateFilter, setDateFilter] = React.useState<string>(defaultView.dateFilter || undefined);
  const [presetFilter, setPresetFilter] = React.useState<string[]>(defaultView.presetFilter || []);
  const [groupBy, setGroupBy] = React.useState<string>(defaultView.groupBy || undefined);
  const [sortBy, setSortBy] = React.useState<string>(defaultView.sortBy || undefined);

  const [selectedWorkspaces, setSelectedWorkspaces] = React.useState<string[]>([]);

  const archiveWorkspaces = (workspaceIds: string[]) => {
    setLoading(true);
    submit({ workspaceIds }, { method: 'POST', action: 'archive', encType: 'application/json' });
  };

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location.pathname) {
      setLoading(false);
      setSelectedWorkspaces([]);
    }
  }, [navigation.state, navigation.formAction, navigation.location?.pathname]);

  React.useEffect(() => {
    window.localStorage.setItem(
      'cara_workspaces_default_view',
      JSON.stringify({ statusFilter, userFilter, dateFilter, presetFilter, groupBy, sortBy })
    );
  }, [statusFilter, userFilter, dateFilter, presetFilter, groupBy, sortBy]);

  const filterWorkspace = (workspace: CaraWorkspaceOverviewFieldsFragment) => {
    const filterSearch = () => {
      if (!search) {
        return true;
      }

      // Search by title, client name, keywords, or user name
      return (
        workspace.title.toLowerCase().includes(search.toLowerCase()) ||
        workspace.metadata.clientName?.toLowerCase().includes(search.toLowerCase()) ||
        workspace.metadata.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(search.toLowerCase())
        ) ||
        workspace.agencyUser.name.toLowerCase().includes(search.toLowerCase())
      );
    };

    const filterStatus = () => {
      if (!statusFilter.length) {
        return true;
      }

      return statusFilter.includes(workspace.state);
    };

    const filterUser = () => {
      if (!userFilter.length) {
        return true;
      }

      return userFilter.includes(workspace.agencyUser.id);
    };

    const filterDate = () => {
      if (!dateFilter) {
        return true;
      }

      switch (dateFilter) {
        case 'Today':
          return new Date(workspace.updatedAt) >= new Date(new Date().setHours(0, 0, 0, 0));
        case 'Yesterday':
          return (
            new Date(workspace.updatedAt) >=
            new Date(new Date().setHours(0, 0, 0, 0) - 24 * 60 * 60 * 1000)
          );
        case 'Past week':
          return (
            new Date(workspace.updatedAt) >=
            new Date(new Date().setHours(0, 0, 0, 0) - 7 * 24 * 60 * 60 * 1000)
          );
        case 'Past month':
          return (
            new Date(workspace.updatedAt) >=
            new Date(new Date().setHours(0, 0, 0, 0) - 30 * 24 * 60 * 60 * 1000)
          );
        case 'Past year':
          return (
            new Date(workspace.updatedAt) >=
            new Date(new Date().setHours(0, 0, 0, 0) - 365 * 24 * 60 * 60 * 1000)
          );
        default:
          return false;
      }
    };

    const filterPreset = () => {
      if (!presetFilter.length) {
        return true;
      }

      return presetFilter.includes(workspace.preset || workspace.customPreset?.id || '');
    };

    const filterFunctions = [filterSearch, filterStatus, filterUser, filterDate, filterPreset];
    return filterFunctions.every((filter) => filter());
  };

  const getGroupingKey = (workspace: CaraWorkspaceOverviewFieldsFragment, groupBy: string) => {
    switch (groupBy) {
      case 'client':
        return {
          key:
            workspace.metadata?.clientName?.replace(/^[a-zA-Z0-9]+/g, '').toLowerCase() || '(none)',
          label: workspace.metadata?.clientName || '(none)'
        };
      case 'preset':
        if (workspace.preset) {
          return { key: workspace.preset, label: titleCase(workspace.preset) };
        }
        if (workspace.customPreset?.title) {
          return { key: workspace.customPreset.id, label: workspace.customPreset.title };
        }
        return { key: '(none)', label: '(none)' };
      case 'user':
        return { key: workspace.agencyUser.id, label: workspace.agencyUser.name };
      case 'date': {
        // Group by "Today", "Yesterday", "Past week", "Past month", "Past year"
        const date = new Date(workspace.updatedAt);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) {
          return { key: 'Today', label: 'Today' };
        }
        if (diffDays <= 2) {
          return { key: 'Yesterday', label: 'Yesterday' };
        }
        if (diffDays <= 7) {
          return { key: 'Past week', label: 'Past week' };
        }
        if (diffDays <= 30) {
          return { key: 'Past month', label: 'Past month' };
        }
        return { key: 'Past year', label: 'Past year' };
      }
      case 'status':
        return {
          key: workspace.state,
          label: getWorkspaceStateTitle(workspace.state)
        };
      default:
        return { key: '', label: '' };
    }
  };

  const getSortFunction = (sortBy: string) => {
    switch (sortBy) {
      case 'client':
        return (a: CaraWorkspaceOverviewFieldsFragment, b: CaraWorkspaceOverviewFieldsFragment) =>
          (a.metadata.clientName || '').localeCompare(b.metadata.clientName || '');
      case 'date':
        return (a: CaraWorkspaceOverviewFieldsFragment, b: CaraWorkspaceOverviewFieldsFragment) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      case 'preset':
        return (a: CaraWorkspaceOverviewFieldsFragment, b: CaraWorkspaceOverviewFieldsFragment) =>
          (a.preset || a.customPreset?.title || '').localeCompare(
            b.preset || b.customPreset?.title || ''
          );
      case 'user':
        return (a: CaraWorkspaceOverviewFieldsFragment, b: CaraWorkspaceOverviewFieldsFragment) =>
          a.agencyUser.name.localeCompare(b.agencyUser.name);
      case 'status':
        return (a: CaraWorkspaceOverviewFieldsFragment, b: CaraWorkspaceOverviewFieldsFragment) =>
          a.state.localeCompare(b.state);
    }
  };

  const groupWorkspaces = (workspaces: CaraWorkspaceOverviewFieldsFragment[]) => {
    const groupedWorkspaces = workspaces.reduce(
      (acc, workspace) => {
        const { key, label } = getGroupingKey(workspace, groupBy);
        acc[key] = { label, workspaces: [...(acc[key]?.workspaces || []), workspace] };
        return acc;
      },
      {} as Record<string, { label: string; workspaces: CaraWorkspaceOverviewFieldsFragment[] }>
    );

    // Sort controls sorting within groups
    const sortedGroups: [
      string,
      { label: string; workspaces: CaraWorkspaceOverviewFieldsFragment[] }
    ][] = Object.entries(groupedWorkspaces).map(([key, workspaces]) => [
      key,
      {
        label: workspaces.label,
        workspaces: sortBy
          ? workspaces.workspaces.sort(getSortFunction(sortBy))
          : workspaces.workspaces
      }
    ]);

    // For now, sort groups by specific logic - in the future we can expose a group sort control.
    sortedGroups.sort(([keyA, { label: labelA }], [keyB, { label: labelB }]) => {
      switch (groupBy) {
        case 'date':
          // Sort by date, but "Today" should be first, then "Yesterday", then "Past week", then "Past month", then "Past year"
          return (
            ['Today', 'Yesterday', 'Past week', 'Past month', 'Past year'].indexOf(keyA) -
            ['Today', 'Yesterday', 'Past week', 'Past month', 'Past year'].indexOf(keyB)
          );
        case 'status':
          // Sort by "Created", "Waiting", "Working", "Idle"
          return (
            ['CREATED', 'WAITING_FOR_INPUT', 'WORKING', 'IDLE'].indexOf(keyA) -
            ['CREATED', 'WAITING_FOR_INPUT', 'WORKING', 'IDLE'].indexOf(keyB)
          );
        default: {
          if (labelA === '(none)') {
            return 1;
          }
          if (labelB === '(none)') {
            return -1;
          }
          return labelA.localeCompare(labelB);
        }
      }
    });

    return sortedGroups;
  };

  return (
    <PageTitle
      title="Workspaces"
      description="Copilot workspaces you've created to interact with Cara"
    >
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full">
              <Fieldset>
                <FieldGroup>
                  <Field className="w-full max-w-md">
                    <Label className="sr-only" data-slot="none">
                      Filter table
                    </Label>
                    <InputGroup>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      <Input
                        type="search"
                        name="search"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        placeholder="Start typing to filter..."
                      />
                    </InputGroup>
                  </Field>
                </FieldGroup>

                <React.Suspense fallback={<></>}>
                  <Await resolve={loader?.data}>
                    {(data) =>
                      !!data.caraWorkspaces && (
                        <div className="mt-2 flex flex-row flex-wrap gap-2">
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Filter by status
                            </Label>
                            <FilterButton
                              label="Status"
                              icon={ClockIcon}
                              multiple
                              initialValues={statusFilter}
                              values={[
                                { label: 'Created', value: CaraWorkspaceState.Created },
                                { label: 'Idle', value: CaraWorkspaceState.Idle },
                                {
                                  label: 'Waiting',
                                  value: CaraWorkspaceState.WaitingForInput
                                },
                                { label: 'Working', value: CaraWorkspaceState.Working }
                              ]}
                              onChange={(values) => setStatusFilter(values)}
                            />
                          </Field>
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Filter by user
                            </Label>
                            <FilterButton
                              label="User"
                              icon={UserIcon}
                              multiple
                              initialValues={userFilter}
                              values={data.caraWorkspaces
                                .map((w) => w.agencyUser)
                                .reduce(
                                  (acc, user) => {
                                    if (!acc.find((u) => u.value === user.id)) {
                                      acc.push({
                                        label: user.name,
                                        value: user.id
                                      });
                                    }
                                    return acc;
                                  },
                                  [] as { label: string; value: string }[]
                                )
                                .sort((a, b) => a.label.localeCompare(b.label))}
                              onChange={(values) => setUserFilter(values)}
                            />
                          </Field>
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Filter by date
                            </Label>
                            <FilterButton
                              label="Date"
                              icon={CalendarIcon}
                              initialValues={dateFilter ? [dateFilter] : []}
                              values={[
                                { label: 'Today', value: 'Today' },
                                {
                                  label: 'Yesterday',
                                  value: 'Yesterday'
                                },
                                {
                                  label: 'Past week',
                                  value: 'Past week'
                                },
                                {
                                  label: 'Past month',
                                  value: 'Past month'
                                },
                                {
                                  label: 'Past year',
                                  value: 'Past year'
                                }
                              ]}
                              onChange={(values) => setDateFilter(values[0])}
                            />
                          </Field>
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Filter by preset
                            </Label>
                            <FilterButton
                              label="Preset"
                              icon={CubeIcon}
                              multiple
                              initialValues={presetFilter}
                              values={data.caraWorkspaces
                                .map((w) => ({
                                  label: w.preset
                                    ? titleCase(w.preset)
                                    : w.customPreset?.title || '',
                                  value: w.preset || w.customPreset?.id || ''
                                }))
                                .filter((p) => !!p.value && !!p.label)
                                .reduce(
                                  (acc, preset) => {
                                    if (!acc.find((p) => p.value === preset.value)) {
                                      acc.push(preset);
                                    }
                                    return acc;
                                  },
                                  [] as { label: string; value: string }[]
                                )
                                .sort((a, b) => a.label.localeCompare(b.label))}
                              onChange={(values) => setPresetFilter(values)}
                            />
                          </Field>
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Group by field
                            </Label>
                            <FilterButton
                              label="Group"
                              icon={Squares2X2Icon}
                              initialValues={groupBy ? [groupBy] : []}
                              values={[
                                { label: 'Client', value: 'client' },
                                { label: 'Date', value: 'date' },
                                { label: 'Preset', value: 'preset' },
                                { label: 'Status', value: 'status' },
                                { label: 'User', value: 'user' }
                              ]}
                              onChange={(values) => setGroupBy(values[0])}
                            />
                          </Field>
                          <Field>
                            <Label className="sr-only" data-slot="none">
                              Sort by field
                            </Label>
                            <FilterButton
                              label="Sort"
                              icon={ArrowsUpDownIcon}
                              initialValues={sortBy ? [sortBy] : []}
                              values={[
                                { label: 'Client', value: 'client' },
                                { label: 'Date', value: 'date' },
                                { label: 'Preset', value: 'preset' },
                                { label: 'Status', value: 'status' },
                                { label: 'User', value: 'user' }
                              ]}
                              onChange={(values) => setSortBy(values[0])}
                            />
                          </Field>
                        </div>
                      )
                    }
                  </Await>
                </React.Suspense>
              </Fieldset>
            </form>
          </div>
        </div>

        <React.Suspense fallback={<CopilotWorkspacesPageLoader />}>
          <Await resolve={loader?.data}>
            {({ caraWorkspaces }) => (
              <div className="relative px-4 sm:px-6 rounded-lg bg-white shadow">
                {!caraWorkspaces?.length && (
                  <div className="text-center py-12">
                    <SparklesIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                      No workspaces found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      Create a copilot workspace to get started.
                    </p>
                  </div>
                )}
                <AnimatePresence>
                  {selectedWorkspaces.length > 0 && (
                    <motion.div
                      className="fixed lg:left-1/2 left-[calc(50%-100px)] bottom-12 shadow-xl border border-neutral-100 rounded-full bg-white p-2 z-10 flex gap-2"
                      initial={{ opacity: 0, translateY: 100, scale: 0.9 }}
                      animate={{ opacity: 1, translateY: 0, scale: 1 }}
                      exit={{ opacity: 0, translateY: 100, scale: 0.9 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                    >
                      <Headless.Button
                        className={clsx(
                          'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                          'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300',
                          'text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90'
                        )}
                        onClick={() => setSelectedWorkspaces([])}
                      >
                        <div>{selectedWorkspaces.length} selected</div>
                        <div>
                          <XMarkIcon className="size-3.5" />
                        </div>
                      </Headless.Button>
                      <Headless.Button
                        className={clsx(
                          'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                          'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300',
                          !loading &&
                            'text-neutral-700 bg-white border border-neutral-300 border-dashed hover:bg-neutral-100/50',
                          loading &&
                            'text-neutral-500 bg-neutral-50/50 border border-neutral-200 border-dashed cursor-default'
                        )}
                        onClick={() => archiveWorkspaces(selectedWorkspaces)}
                      >
                        <TrashIcon className="size-3.5" />
                        Archive
                        {loading && <Spinner size="size-3.5" color="neutral" />}
                      </Headless.Button>
                    </motion.div>
                  )}
                </AnimatePresence>
                {!!caraWorkspaces?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4 pr-0">
                          <Checkbox
                            checked={
                              selectedWorkspaces.length > 0 &&
                              selectedWorkspaces.length ===
                                caraWorkspaces.filter(filterWorkspace).length
                            }
                            color="sky"
                            onChange={(v) => {
                              setSelectedWorkspaces((prev) =>
                                v
                                  ? uniq([
                                      ...prev,
                                      ...caraWorkspaces.filter(filterWorkspace).map((w) => w.id)
                                    ])
                                  : []
                              );
                            }}
                          />
                        </TableHeader>
                        <TableHeader className="py-4">Title</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Client</TableHeader>
                        <TableHeader className="py-4">User</TableHeader>
                        <TableHeader className="py-4">Last Updated</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupWorkspaces(caraWorkspaces.filter(filterWorkspace)).map(([key, ws]) => (
                        <React.Fragment key={key}>
                          {key && (
                            <tr>
                              <th className="border-b border-b-neutral-950/10 bg-neutral-100 py-2 pl-6 pr-0 font-medium dark:border-b-white/10">
                                <Checkbox
                                  checked={
                                    selectedWorkspaces.length > 0 &&
                                    ws.workspaces.every((w) => selectedWorkspaces.includes(w.id))
                                  }
                                  color="sky"
                                  onChange={(v) => {
                                    setSelectedWorkspaces((prev) =>
                                      v
                                        ? uniq([...prev, ...ws.workspaces.map((w) => w.id)])
                                        : prev.filter((p) => !ws.workspaces.some((w) => w.id === p))
                                    );
                                  }}
                                />
                              </th>
                              <th
                                className="border-b border-b-neutral-950/10 bg-neutral-100 py-2 px-4 font-medium dark:border-b-white/10"
                                colSpan={5}
                              >
                                {ws.label}
                              </th>
                            </tr>
                          )}
                          {ws.workspaces.map((workspace) => (
                            <TableRow key={workspace.id} href={`/app/workspaces/${workspace.id}`}>
                              <TableCell
                                className="pr-0"
                                valign="top"
                                noHref
                                onClick={() => {
                                  setSelectedWorkspaces((prev) =>
                                    prev.includes(workspace.id)
                                      ? prev.filter((id) => id !== workspace.id)
                                      : uniq([...prev, workspace.id])
                                  );
                                }}
                              >
                                <Checkbox
                                  checked={selectedWorkspaces.includes(workspace.id)}
                                  color="sky"
                                  onChange={(v) => {
                                    setSelectedWorkspaces((prev) =>
                                      v
                                        ? prev.filter((id) => id !== workspace.id)
                                        : uniq([...prev, workspace.id])
                                    );
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  {workspace.title ? (
                                    <div className="text-sm font-medium text-neutral-900">
                                      {workspace.title}
                                    </div>
                                  ) : (
                                    <div className="text-neutral-300">—</div>
                                  )}
                                  {workspace.preset || workspace.customPreset?.title ? (
                                    <div className="text-xs text-neutral-500">
                                      {titleCase(
                                        workspace.preset || workspace.customPreset?.title || ''
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-neutral-300">—</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <WorkspaceStateBadge state={workspace.state} />
                              </TableCell>
                              <TableCell>
                                <div className="truncate max-w-[240px]">
                                  {workspace.metadata.clientName || (
                                    <div className="text-neutral-300">—</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{workspace.agencyUser.name}</TableCell>
                              <TableCell>
                                {workspace.updatedAt &&
                                  new Intl.DateTimeFormat('en-US', {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  }).format(new Date(workspace.updatedAt))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
    </PageTitle>
  );
};

export const CopilotWorkspacesPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading Cara workspaces...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const CopilotWorkspacePage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          caraWorkspace?: CaraWorkspaceFieldsFragment;
          htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
      <Await resolve={loader?.data}>
        {(data) =>
          data?.caraWorkspace && (
            <CopilotWorkspacePageInner
              workspace={data.caraWorkspace}
              htmlDocumentTemplates={data.htmlDocumentTemplates || []}
            />
          )
        }
      </Await>
    </React.Suspense>
  );
};

const CopilotWorkspacePageInner = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
}) => {
  const [workspace, setWorkspace] = React.useState<CaraWorkspaceFieldsFragment>(props.workspace);
  const { data, error, restart } = useSubscription<
    WatchCaraWorkspaceSubscription,
    WatchCaraWorkspaceSubscriptionVariables
  >(WatchCaraWorkspaceDocument, {
    variables: { id: workspace.id },
    client: client,
    onComplete: () => setTimeout(() => restart(), 1000)
  });
  const [, setErrorCount] = React.useState(0);
  const [titleUpdated, setTitleUpdated] = React.useState(false);

  React.useEffect(() => {
    if (data?.watchCaraWorkspace) {
      setWorkspace(data.watchCaraWorkspace);
    }

    setErrorCount((prev) => {
      let next = 0;
      if (error) {
        next = prev + 1;
        setTimeout(() => restart(), Math.min(Math.pow(2, next) * 1000, 10000));
      }

      return next;
    });
  }, [data?.watchCaraWorkspace?.updatedAt, error]);

  React.useEffect(() => {
    if (titleUpdated) {
      const interval = setInterval(() => setTitleUpdated(false), 3000);
      return () => clearInterval(interval);
    }
  }, [titleUpdated]);

  const updateWorkspace = async () => {
    const res = await client.query<GetCaraWorkspaceQuery>({
      query: GetCaraWorkspaceDocument,
      variables: { id: workspace.id }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    if (!res.data?.caraWorkspace) {
      throw new Error('Workspace not found');
    }

    restart();
    setWorkspace(res.data?.caraWorkspace);
    return res.data?.caraWorkspace;
  };

  const updateWorkspaceTitle = async (title: string) => {
    await client.mutate<UpdateCaraWorkspacesMutation, UpdateCaraWorkspacesMutationVariables>({
      mutation: UpdateCaraWorkspacesDocument,
      variables: {
        workspaceIds: [workspace.id],
        input: {
          title: title.trim()
        }
      }
    });

    setTitleUpdated(true);
  };

  const addCaraWorkspaceMessage = async (query: string, attachments: File[]) => {
    if (!workspace) {
      return;
    }

    const res = await client.mutate<AddCaraWorkspaceMessageMutation>({
      mutation: AddCaraWorkspaceMessageDocument,
      variables: {
        id: workspace.id,
        input: {
          message: query,
          files: attachments
        }
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    if (!res.data?.addCaraWorkspaceMessage) {
      throw new Error('Failed to add message to workspace');
    }

    setWorkspace(res.data?.addCaraWorkspaceMessage);
  };

  const [showResolveServiceRequest, setShowResolveServiceRequest] = React.useState(false);
  const [resolveServiceRequestLoading, setResolveServiceRequestLoading] = React.useState(false);

  const handleResolveServiceRequest = async (form: ResolveServiceRequestInput) => {
    setResolveServiceRequestLoading(true);
    try {
      await client.mutate<ResolveServiceRequestMutation, ResolveServiceRequestMutationVariables>({
        mutation: ResolveServiceRequestDocument,
        variables: {
          id: workspace.serviceRequest?.id || '',
          input: {
            notes: form.notes,
            files: form.files
          }
        }
      });
      await updateWorkspace();
      setShowResolveServiceRequest(false);
      setResolveServiceRequestLoading(false);
    } finally {
      setResolveServiceRequestLoading(false);
    }
  };

  return (
    <div>
      {!workspace && <Spinner size="size-8" color="neutral" />}
      {workspace && (
        <div className="-m-12">
          <div className="xl:hidden flex w-full py-2 px-8 flex-row items-center justify-between bg-white border-b border-neutral-200 sticky top-16 lg:top-0 z-10">
            <div className="flex flex-row items-center gap-2">
              <Link
                to="/app/workspaces"
                className="rounded-full p-2 flex items-center justify-center hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900 transition-all duration-150 cursor-pointer"
              >
                <ChevronLeftIcon className="size-4" />
              </Link>
              <div className="text-sm text-neutral-700 font-medium">{workspace.title}</div>
            </div>
          </div>
          <div className="flex flex-row">
            <div className="m-auto px-12 mt-6 w-full max-w-4xl relative">
              <div className="w-full px-6 flex flex-col gap-4">
                {workspace.schemaVersion === CaraWorkspaceSchemaVersion.V0 &&
                  formatWorkspaceV0(workspace, props.htmlDocumentTemplates)}
                {workspace.schemaVersion === CaraWorkspaceSchemaVersion.V1 &&
                  formatWorkspaceV1(workspace, props.htmlDocumentTemplates)}
                <motion.div
                  animate={{
                    height: workspace.state === CaraWorkspaceState.Working ? '240px' : 0,
                    opacity: workspace.state === CaraWorkspaceState.Working ? 1 : 0
                  }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
              <div className="w-full sticky bottom-0 left-0">
                <AnimateIn className="pb-8 bg-neutral-50">
                  <CaraInput
                    onSubmit={addCaraWorkspaceMessage}
                    inline
                    loading={workspace?.state === CaraWorkspaceState.Working}
                  />
                </AnimateIn>
              </div>
            </div>
            <div className="hidden xl:block h-screen sticky top-0 right-0 w-full max-w-80 p-8 bg-white border-l border-neutral-200">
              <div className="flex flex-row items-center gap-2">
                <Link
                  to="/app/workspaces"
                  className="rounded-full -ml-3 py-1.5 pl-2 pr-3 flex items-center justify-center gap-2 hover:bg-neutral-200/65 text-neutral-700 hover:text-neutral-900 transition-all duration-150 cursor-pointer"
                >
                  <ChevronLeftIcon className="size-4" />
                  <div className="text-xs">Back to workspaces</div>
                </Link>
              </div>
              {!workspace.title && <h1 className="text-lg font-medium text-neutral-700">—</h1>}
              {!!workspace.title && (
                <h1
                  className="group text-lg font-medium text-neutral-700 cursor-pointer focus:px-2 focus:-mx-2"
                  contentEditable
                  onBlur={(e) => {
                    const title = e.currentTarget.innerText;
                    if (title.trim() !== '') {
                      updateWorkspaceTitle(title);
                    }
                  }}
                >
                  {workspace.title}
                  {!titleUpdated && (
                    <PencilIcon className="group-focus:hidden group-hover:inline hidden mb-1 ml-1 size-3.5 text-neutral-400" />
                  )}
                  {titleUpdated && (
                    <CheckIcon className="mb-1 ml-1 inline size-3.5 text-emerald-500" />
                  )}
                </h1>
              )}
              <div className="mt-2">
                <WorkspaceStateBadge state={workspace.state} />
              </div>
              <dl>
                <div className="flex flex-col gap-6 pb-6 pt-6">
                  {workspace.preset && (
                    <div>
                      <dt className="text-sm/6 font-medium text-neutral-900">Preset</dt>
                      <dd className="text-sm/6 text-neutral-900 mt-1">
                        {titleCase(workspace.preset)}
                      </dd>
                    </div>
                  )}
                  {workspace.customPreset && (
                    <div>
                      <dt className="text-sm/6 font-medium text-neutral-900">Custom Preset</dt>
                      <dd className="text-sm/6 text-neutral-900 mt-1">
                        <a
                          href={`/app/presets/${workspace.customPreset.id}/edit`}
                          className="text-primary-600 hover:text-primary-900 hover:underline"
                        >
                          {workspace.customPreset.title}
                        </a>
                      </dd>
                    </div>
                  )}
                  {workspace.metadata.clientName && (
                    <div>
                      <dt className="text-sm/6 font-medium text-neutral-900">Client</dt>
                      <dd className="text-sm/6 text-neutral-900 mt-1">
                        {workspace.metadata.clientName}
                      </dd>
                    </div>
                  )}
                  {workspace.files.length > 0 && (
                    <div>
                      <dt className="text-sm/6 font-medium text-neutral-900">Attachments</dt>
                      <dd className="flex flex-row gap-2 flex-wrap mt-1">
                        {workspace.files.map((file) => (
                          <Link
                            key={file.id}
                            to={file.url || ''}
                            title={file.name}
                            target="_blank"
                            className="rounded-full border border-neutral-200 bg-white px-3 py-1 cursor-default flex flex-row items-center gap-1 hover:shadow hover:cursor-pointer transition-all duration-150 overflow-hidden"
                          >
                            <PaperClipIcon className="size-3 flex-shrink-0 text-neutral-500" />
                            <p className="text-sm flex-auto text-neutral-500 truncate">
                              {file.name}
                            </p>
                          </Link>
                        ))}
                      </dd>
                    </div>
                  )}
                  {workspace.serviceRequest && (
                    <div>
                      <dt className="text-sm/6 font-medium text-neutral-900">
                        Service Request: {titleCase(workspace.serviceRequest.type)}
                      </dt>
                      <dd className="mt-1">
                        <div className="text-sm/5 text-neutral-500">
                          {workspace.serviceRequest.details ||
                            workspace.serviceRequest.question?.question ||
                            workspace.serviceRequest.cancelPolicy?.reason}
                        </div>
                        <div className="flex flex-row gap-2 mt-2">
                          <Button
                            color="sky"
                            onClick={() => setShowResolveServiceRequest(true)}
                            disabled={workspace.serviceRequest.state === ServiceRequestState.Closed}
                          >
                            {workspace.serviceRequest.state === ServiceRequestState.Closed
                              ? 'Resolved'
                              : 'Resolve'}
                          </Button>
                          <Button
                            color="white"
                            href={`/app/servicing/${workspace.serviceRequest.id}/overview`}
                          >
                            View
                          </Button>
                        </div>
                      </dd>
                    </div>
                  )}
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
      {workspace && (
        <ResolveServiceRequestDialog
          open={showResolveServiceRequest}
          onClose={() => setShowResolveServiceRequest(false)}
          loading={resolveServiceRequestLoading}
          onSubmit={handleResolveServiceRequest}
          validationErrors={[]}
        />
      )}
    </div>
  );
};

const InProgressIndicator = () => (
  <div className="relative size-2.5">
    <svg
      viewBox="0 0 10 10"
      aria-hidden="true"
      className="size-2.5 absolute top-0 left-0 z-0 animate-ping"
    >
      <circle r={5} cx={5} cy={5} className="fill-blue-300 opacity-95" />
    </svg>
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0">
      <circle r={4} cx={5} cy={5} className="fill-blue-400" />
    </svg>
  </div>
);

const CompletedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-green-300" />
    </svg>
  </div>
);

const FailedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-red-300" />
    </svg>
  </div>
);

const formatWorkspaceV0 = (
  workspace: CaraWorkspaceFieldsFragment | null,
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[]
) => {
  if (!workspace) {
    return [];
  }

  // Sort messages and stages by timestamp
  const combined = [...workspace.messages, ...workspace.planStages].sort((a, b) => {
    const aDate =
      a.__typename === 'CaraWorkspaceMessage'
        ? new Date(a.timestamp)
        : a.__typename === 'CaraWorkspacePlanStage'
          ? new Date(a.events.at(-1)?.startedAt || a.startedAt || a.createdAt)
          : null;
    const bDate =
      b.__typename === 'CaraWorkspaceMessage'
        ? new Date(b.timestamp)
        : b.__typename === 'CaraWorkspacePlanStage'
          ? new Date(b.events.at(-1)?.startedAt || b.startedAt || b.createdAt)
          : null;

    return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
  });

  // Group them by role
  const groups: {
    role: CaraWorkspaceMessageRole;
    items: (
      | CaraWorkspaceFieldsFragment['messages'][0]
      | CaraWorkspaceFieldsFragment['planStages'][0]
    )[];
  }[] = [];

  combined.forEach((item) => {
    if (item.__typename === 'CaraWorkspaceMessage') {
      if (groups.at(-1)?.role === item.role) {
        groups.at(-1)?.items.push(item);
      } else {
        groups.push({ role: item.role, items: [item] });
      }
    } else {
      if (groups.at(-1)?.role === CaraWorkspaceMessageRole.Agent) {
        groups.at(-1)?.items.push(item);
      } else {
        groups.push({ role: CaraWorkspaceMessageRole.Agent, items: [item] });
      }
    }
  });

  // Assign artifacts to the latest plan stage that produced it
  const messageToArtifacts = new Map<string, CaraWorkspaceFieldsFragment['artifacts'][0][]>();
  workspace.artifacts.forEach((artifact) => {
    // Find the stage whose updatedAt is the closest to the artifact's updatedAt
    const stage = workspace.messages.reduce((prev, current) => {
      const currentDate = new Date(current.timestamp);
      const prevDate = new Date(prev.timestamp);
      const artifactDate = new Date(artifact.createdAt);

      return Math.abs(currentDate.getTime() - artifactDate.getTime()) <
        Math.abs(prevDate.getTime() - artifactDate.getTime())
        ? current
        : prev;
    }, workspace.messages[0]);

    messageToArtifacts.set(stage.id, [...(messageToArtifacts.get(stage.id) || []), artifact]);
  });

  // Determine UI quirks that need to be fixed.
  // 1. If the workspace is WORKING and there is no IN_PROGRESS thought, we need to show a "thinking" indicator
  // 2. Display artifacts with the result of the latest plan stage that generated it.
  const thinkingIndicator =
    workspace.state === CaraWorkspaceState.Working &&
    !workspace.planStages.some((stage) =>
      stage.events.some((e) => e.type === CaraWorkspacePlanStageEventType.Thought && !e.endedAt)
    );

  // Now render!
  return groups.map((group, i) => (
    <div key={i} className="flex flex-col gap-2">
      <AnimateIn className={clsx('text-xs text-neutral-500 uppercase font-medium')}>
        {group.role === CaraWorkspaceMessageRole.User ? 'You' : 'Cara'}
      </AnimateIn>
      <div className="flex flex-col gap-2">
        {group.items.map((item, j) => {
          if (
            item.__typename === 'CaraWorkspaceMessage' &&
            group.role === CaraWorkspaceMessageRole.User
          ) {
            return <UserMessage key={item.id} content={item.content} attachments={item.files} />;
          }

          if (
            item.__typename === 'CaraWorkspaceMessage' &&
            group.role === CaraWorkspaceMessageRole.Agent
          ) {
            return (
              <div className="flex flex-col gap-2" key={item.id}>
                <ResponseMessage
                  htmlDocumentTemplates={htmlDocumentTemplates}
                  artifacts={messageToArtifacts.get(item.id) || []}
                  content={item.content}
                  workspaceId={workspace.id}
                />
                {thinkingIndicator && i === groups.length - 1 && j === group.items.length - 1 && (
                  <PlanStageThought title="Thinking..." content="" completed={false} />
                )}
              </div>
            );
          }

          if (item.__typename === 'CaraWorkspacePlanStage') {
            if (item.type === CaraWorkspacePlanStageType.ProcessAttachments) {
              const title =
                item.state === CaraWorkspacePlanStageState.InProgress
                  ? `Processing attachments...`
                  : item.state === CaraWorkspacePlanStageState.Failed
                    ? 'Failed to process attachments'
                    : 'Processed attachments';

              const content =
                [...(item.processAttachmentsDetails?.progress || [])]
                  .sort((a, b) => a.file.name.localeCompare(b.file.name))
                  .map((p) => `**${p.file.name}**: (${Math.round(p.progress * 1000) / 10}%)`)
                  .join('\n\n') || '';

              return (
                <div className="flex flex-col gap-2" key={item.id}>
                  <PlanStageThought
                    title={title}
                    content={content}
                    completed={item.state === CaraWorkspacePlanStageState.Success}
                    error={item.state === CaraWorkspacePlanStageState.Failed}
                  />
                </div>
              );
            }

            if (item.type === CaraWorkspacePlanStageType.Task) {
              const thinkingEvents = getThinkingEventsV0(item);
              if (thinkingEvents.length > 0 || thinkingIndicator) {
                return (
                  <div className="flex flex-col gap-2" key={item.id}>
                    {thinkingEvents.map((e, k) => (
                      <PlanStageThought
                        key={e.id}
                        title={e.title || 'Thinking...'}
                        content={e.content || ''}
                        completed={
                          !thinkingIndicator
                            ? !!e.endedAt
                            : !(
                                i === groups.length - 1 &&
                                j === group.items.length - 1 &&
                                k === thinkingEvents.length - 1
                              )
                        }
                      />
                    ))}
                    {thinkingEvents.length === 0 &&
                      thinkingIndicator &&
                      i === groups.length - 1 &&
                      j === group.items.length - 1 && (
                        <PlanStageThought title="Thinking..." content="" completed={false} />
                      )}
                  </div>
                );
              }
            }
          }

          return null;
        })}
      </div>
    </div>
  ));
};

const getThinkingEventsV0 = (stage: CaraWorkspaceFieldsFragment['planStages'][0]) => {
  const parseArguments = (s?: string) => {
    try {
      return JSON.parse(s || '{}');
    } catch (e) {
      return {};
    }
  };

  const formatBatchSearchFileArguments = (s?: string) => {
    const args = parseArguments(s || '{}');
    if (!args.queries?.queries?.length) return '';
    return `Searching files for ${args.queries?.queries?.map((q) => q.search_query).join(', ')}.`;
  };

  const formatWebResearchArguments = (
    event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]
  ) => {
    const args = parseArguments(event.toolCall?.argumentsJson || '{}');
    const results = parseArguments(event.toolCall?.result || '[]');

    if (results.length > 0) {
      return results
        .map((r) => `# ${r.query}\n\n${r.result?.choices?.[0]?.message?.content}`)
        .join('\n\n');
    } else if (args.queries?.queries?.length) {
      return args.queries.queries.map((q) => `# ${q.query}`).join('\n');
    }

    return '';
  };

  const formatBatchSearchKnowledgeBaseArguments = (s?: string) => {
    const args = parseArguments(s || '{}');
    if (!args.queries?.queries?.length) return '';
    return `Searching knowledge base for ${args.queries?.queries?.map((q) => q.query).join(', ')}.`;
  };

  const supportedToolEvents = {
    draft_email: (event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]) => ({
      id: event.id,
      title: 'Drafting email',
      content: '',
      endedAt: event.endedAt
    }),
    draft_document: (event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]) => ({
      id: event.id,
      title: 'Generating document',
      content: '',
      endedAt: event.endedAt
    }),
    batch_search_file: (event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]) => ({
      id: event.id,
      title: 'Searching files',
      content: formatBatchSearchFileArguments(event.toolCall?.argumentsJson || '{}'),
      endedAt: event.endedAt
    }),
    batch_web_research: (event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]) => ({
      id: event.id,
      title: 'Performing web research',
      content: formatWebResearchArguments(event),
      endedAt: event.endedAt
    }),
    batch_search_knowledge_base: (
      event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]
    ) => ({
      id: event.id,
      title: 'Searching knowledge base',
      content: formatBatchSearchKnowledgeBaseArguments(event.toolCall?.argumentsJson || '{}'),
      endedAt: event.endedAt
    }),

    ...Object.fromEntries(
      Object.values(DocumentType).map((t) => [
        `generate_document_${t.toLowerCase()}`,
        (event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]) => ({
          id: event.id,
          title: `Generating ${t.replace('_', ' ')} document`,
          content: '',
          endedAt: event.endedAt
        })
      ])
    )
  };

  return stage.events
    .filter(
      (e) =>
        (e.type === CaraWorkspacePlanStageEventType.Thought && !!e.title) ||
        (e.type === CaraWorkspacePlanStageEventType.ToolCall &&
          !!supportedToolEvents[e.toolCall?.name || '']?.(e))
    )
    .map((e) =>
      e.type === CaraWorkspacePlanStageEventType.ToolCall
        ? supportedToolEvents[e.toolCall?.name || ''](e)
        : e
    )
    .filter((e) => e !== null);
};

const PlanStageThought = (props: {
  title: string;
  content: string;
  completed: boolean;
  overrideOpen?: boolean;
  error?: boolean | string | null;
}) => {
  const [showContent, setShowContent] = React.useState(!props.completed || props.overrideOpen);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (props.completed) {
      if (!props.overrideOpen) {
        setShowContent(false);
      } else {
        timeout = setTimeout(() => {
          setShowContent(false);
        }, 3000);
      }
    }

    return () => clearTimeout(timeout);
  }, [props.completed, props.overrideOpen]);

  return (
    <AnimateIn>
      <Headless.Button
        className="w-full flex flex-row items-center justify-between"
        onClick={() => setShowContent(!showContent)}
        disabled={!props.content}
      >
        <div className="inline-flex items-center gap-x-1.5 text-sm font-medium text-neutral-700">
          {props.error ? (
            <FailedIndicator />
          ) : props.completed ? (
            <CompletedIndicator />
          ) : (
            <InProgressIndicator />
          )}
          {props.title}
        </div>
        <div className={clsx(!props.content && 'hidden')}>
          {showContent ? (
            <ChevronUpIcon className="size-4 text-neutral-500" />
          ) : (
            <ChevronDownIcon className="size-4 text-neutral-500" />
          )}
        </div>
      </Headless.Button>
      <motion.div
        animate={{
          height: showContent ? 'auto' : 0,
          opacity: showContent ? 1 : 0
        }}
        initial={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden unstyled-markdown-container"
      >
        <Markdown
          remarkPlugins={[[remarkGfm, {}]]}
          rehypePlugins={[
            [rehypeRaw, { tagFilter: true }],
            [rehypeSanitize, {}]
          ]}
        >
          {props.content.replace(/<br>/g, ' ')}
        </Markdown>
        {!props.completed && !props.error && (
          <div className="flex flex-row items-center gap-2 mt-2">
            <Spinner size="size-4" color="neutral" />
          </div>
        )}
      </motion.div>
    </AnimateIn>
  );
};

const UserMessage = (props: {
  content: string;
  attachments: CaraWorkspaceFieldsFragment['messages'][0]['files'];
}) => (
  <AnimateIn>
    <div
      className={clsx(
        'markdown-container p-5 rounded-lg shadow whitespace-break-spaces w-full',
        'bg-primary-500 text-white self-start'
      )}
    >
      <Markdown
        remarkPlugins={[[remarkGfm, {}]]}
        rehypePlugins={[
          [rehypeRaw, { tagFilter: true }],
          [rehypeSanitize, {}]
        ]}
      >
        {props.content.replace(/<br>/g, ' ')}
      </Markdown>
    </div>
    {props.attachments.length >= 1 && (
      <div className="flex flex-wrap flex-row gap-2 mt-2">
        {props.attachments.map((attachment) => (
          <Link
            key={attachment.id}
            to={attachment.url || ''}
            target="_blank"
            className="rounded-full border border-neutral-200 bg-white px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1 hover:shadow hover:cursor-pointer transition-all duration-150"
          >
            <PaperClipIcon className="size-3 text-neutral-500" />
            <p className="text-sm text-neutral-500">{attachment.name}</p>
          </Link>
        ))}
      </div>
    )}
  </AnimateIn>
);

const ResponseMessage = (props: {
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  artifacts: CaraWorkspaceFieldsFragment['artifacts'][0][];
  content: string;
  workspaceId: string;
  loading?: boolean;
}) => {
  const [copied, setCopied] = React.useState(false);
  const copyText = async () => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(props.content);
    navigator.clipboard.writeText(text.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const rewriteFootnotes = () => {
    document.querySelectorAll('.markdown-container').forEach((container) => {
      container.querySelectorAll('sup').forEach((footnote) => {
        // Get the href from the containing anchor element
        const a = footnote.querySelector('a');
        if (!a) {
          return;
        }

        // Find the element that describes the footnote contents
        const footnoteIndex = a.innerText;
        if (!footnoteIndex) {
          return;
        }

        const describedByElement = container.querySelector(
          `li#user-content-user-content-fn-${footnoteIndex} a`
        );
        if (!describedByElement) {
          return;
        }

        const href = describedByElement.getAttribute('href');
        if (!href) {
          return;
        }

        // Update the link to the href
        a.setAttribute('href', href);
        a.setAttribute('target', '_blank');
      });
    });
  };

  React.useEffect(() => {
    // Set up a document mutation observer on .markdown-container
    const observer = new MutationObserver(() => setTimeout(() => rewriteFootnotes(), 1000));
    const containers = document.querySelectorAll('.markdown-container');
    containers.forEach((container) => {
      observer.observe(container as Node, {
        childList: true,
        subtree: true
      });
    });

    // Perform initial rewrite
    rewriteFootnotes();

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => rewriteFootnotes(), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AnimateIn>
      <div
        className={clsx(
          'markdown-container p-5 rounded-lg shadow w-full relative',
          'bg-white text-neutral-700 self-start'
        )}
      >
        {!!props.content && (
          <div className="absolute top-3 right-3 flex flex-row gap-2 rounded-full bg-white">
            <Headless.Menu as="div" className="relative">
              <Headless.MenuButton className="inline-flex items-center rounded-full p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-150">
                <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
              </Headless.MenuButton>

              <Headless.MenuItems
                transition
                className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <Headless.MenuItem>
                  <div
                    className="flex flex-row items-center gap-2 px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                    onClick={() => copyText()}
                  >
                    {copied ? (
                      <CheckIcon className="size-4 text-neutral-500" />
                    ) : (
                      <ClipboardDocumentIcon className="size-4 text-neutral-500" />
                    )}
                    Copy as plain text
                  </div>
                </Headless.MenuItem>
              </Headless.MenuItems>
            </Headless.Menu>
          </div>
        )}
        <AnimatePresence mode="wait">
          {!!props.content && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{
                height: { duration: 0.15, ease: 'anticipate' },
                opacity: { duration: 0.3, ease: 'anticipate' }
              }}
              exit={{ height: 0, opacity: 0 }}
            >
              <Markdown
                remarkPlugins={[[remarkGfm, {}]]}
                rehypePlugins={[
                  [rehypeRaw, { tagFilter: true }],
                  [rehypeSanitize, {}]
                ]}
              >
                {props.content.replace(/<br>/g, ' ').replace(/```/g, '')}
              </Markdown>
              {props.loading && (
                <div className="flex flex-row justify-start mt-1.5">
                  <Spinner size="size-4" color="neutral" />
                </div>
              )}
            </motion.div>
          )}
          {!props.content && (
            <motion.div
              className="flex-1 truncate"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{
                height: { duration: 0.15, ease: 'anticipate' },
                opacity: { duration: 0.3, ease: 'anticipate' }
              }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="h-3 mt-0 w-full bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
              <div className="h-3 mt-1.5 w-3/4 bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {props.artifacts.length > 0 && (
        <div className="flex flex-col gap-2 flex-wrap mt-2">
          {props.artifacts.map((artifact) => (
            <ResponseArtifact
              key={artifact.id}
              workspaceId={props.workspaceId}
              artifact={artifact}
              htmlDocumentTemplates={props.htmlDocumentTemplates}
            />
          ))}
        </div>
      )}
    </AnimateIn>
  );
};

const ResponseArtifact = (props: {
  workspaceId: string;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
}) => {
  const [copied, setCopied] = React.useState(false);
  const [emailBody, setEmailBody] = React.useState('');

  const [showHtmlDocument, setShowHtmlDocument] = React.useState(false);
  const [selectedStyleId, setSelectedStyleId] = React.useState<string>('');

  React.useEffect(() => {
    const fetchEmailBody = async () => {
      if (props.artifact?.email?.body) {
        const text = await remark()
          .use(stripMarkdown, {
            keep: ['list', 'listItem']
          })
          .process(props.artifact.email.body);
        setEmailBody(text.toString());
      }
    };

    fetchEmailBody();
  }, [props.artifact?.email?.body]);

  const copyText = async (input: string) => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(input);
    navigator.clipboard.writeText(text.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openEmailClient = async (email: CaraWorkspaceFieldsFragment['artifacts'][0]['email']) => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(email?.body || '');

    window.open(
      `mailto:${encodeURIComponent(email?.to?.[0]?.email || '')}?subject=${encodeURIComponent(
        email?.subject || ''
      )}&body=${encodeURIComponent(text.toString())}`,
      '_blank'
    );
  };

  // Depending on the artifact type we need to build certain UI. Support emails only for now
  if (props.artifact.type === CaraWorkspaceArtifactType.Email) {
    return (
      <AnimateIn className="rounded-lg shadow bg-white p-4 max-w-xl relative">
        <div className="absolute top-3 right-3 flex flex-row gap-2">
          <Headless.Menu as="div" className="relative">
            <Headless.MenuButton className="inline-flex items-center rounded-full p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-150">
              <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
            </Headless.MenuButton>

            <Headless.MenuItems
              transition
              className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <Headless.MenuItem>
                <div
                  className="flex flex-row items-center gap-2 px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                  onClick={() => copyText(props.artifact.email?.body || '')}
                >
                  {copied ? (
                    <CheckIcon className="size-4 text-neutral-500" />
                  ) : (
                    <ClipboardDocumentIcon className="size-4 text-neutral-500" />
                  )}
                  Copy as plain text
                </div>
              </Headless.MenuItem>
              <Headless.MenuItem>
                <div
                  className="flex flex-row items-center gap-2 px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                  onClick={() => openEmailClient(props.artifact.email)}
                >
                  <EnvelopeIcon className="size-4 text-neutral-500" />
                  Open in email client
                </div>
              </Headless.MenuItem>
            </Headless.MenuItems>
          </Headless.Menu>
        </div>
        <p className="text-sm font-medium text-neutral-700 pr-20">
          {props.artifact.email?.subject}
        </p>
        <p className="text-sm text-neutral-500 pr-20">{props.artifact.email?.to?.[0]?.email}</p>
        <pre className="text-sm font-body mt-4 text-neutral-700 whitespace-break-spaces">
          {emailBody}
        </pre>
      </AnimateIn>
    );
  }

  if (props.artifact.type === CaraWorkspaceArtifactType.Document) {
    return (
      <AnimateIn className="rounded-lg shadow bg-white p-4 max-w-xl">
        <div className="flex justify-between">
          <div className="flex flex-row gap-2">
            <PaperClipIcon className="size-6 text-neutral-500" />
            <div>
              <p className="text-sm font-medium text-neutral-700">
                {props.artifact.document?.type.replace('_', ' ')}
              </p>
              <p className="text-sm text-neutral-500">
                {props.artifact.document?.data?.acord125?.applicant1.name ||
                  props.artifact.document?.data?.acord126?.namedInsured ||
                  props.artifact.document?.data?.acord130?.namedInsured?.fullName ||
                  props.artifact.document?.data?.acord131?.namedInsured ||
                  props.artifact.document?.data?.acord140?.namedInsured ||
                  props.artifact.document?.data?.acord25?.holder.name ||
                  ''}
              </p>
            </div>
          </div>
          <Button plain target="_blank" href={props.artifact.document?.downloadUrl || ''}>
            View Document
          </Button>
        </div>
      </AnimateIn>
    );
  }

  if (props.artifact.type === CaraWorkspaceArtifactType.HtmlDocument) {
    return (
      <>
        <Dialog open={showHtmlDocument} onClose={() => setShowHtmlDocument(false)} size="4xl">
          <DialogTitle>{props.artifact.htmlDocument?.title}</DialogTitle>
          <DialogBody>
            <Select
              value={selectedStyleId}
              onChange={(e) => setSelectedStyleId(e.target.value)}
              className="max-w-80"
            >
              <option value="">None</option>
              <optgroup label="Built-in styles">
                <option value="builtin:classic-professional">Classic Professional</option>
                <option value="builtin:modern-minimalist">Modern Minimalist</option>
                <option value="builtin:warm-human">Warm Human</option>
                <option value="builtin:elegant-premium">Elegant Premium</option>
                <option value="builtin:technical-structured">Technical and Structured</option>
              </optgroup>
              <optgroup label="Custom styles">
                {props.htmlDocumentTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.title}
                  </option>
                ))}
                {props.htmlDocumentTemplates.length === 0 && (
                  <option disabled>No custom styles found</option>
                )}
              </optgroup>
            </Select>
            <iframe
              src={`${getStaticsBaseUrl()}/agency/html/${props.workspaceId}/${props.artifact.id}?style_id=${selectedStyleId}`}
              className="h-[calc(100vh-16rem)] w-full mt-4 rounded-lg bg-neutral-50 border border-neutral-100"
              title={props.artifact.htmlDocument?.title}
            />
          </DialogBody>
        </Dialog>
        <AnimateIn className="rounded-lg shadow bg-white p-4 max-w-xl">
          <div className="flex justify-between gap-4">
            <div className="flex flex-row gap-2">
              <PaperClipIcon className="size-6 text-neutral-500" />
              <div>
                <p className="text-sm font-medium text-neutral-700">
                  {props.artifact.htmlDocument?.title}
                </p>
              </div>
            </div>
            <Button
              plain
              target="_blank"
              onClick={() => setShowHtmlDocument(true)}
              className="flex-none"
            >
              Preview Document
            </Button>
          </div>
        </AnimateIn>
      </>
    );
  }

  return null;
};

const formatWorkspaceV1 = (
  workspace: CaraWorkspaceFieldsFragment,
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[]
) => {
  if (!workspace) {
    return [];
  }

  // Sort messages and stages by timestamp
  const combined = [
    ...workspace.messages,
    ...workspace.planStages.filter((p) => p.type === CaraWorkspacePlanStageType.ProcessAttachments),
    ...workspace.planStages.flatMap((p) => p.events)
  ].sort((a, b) => {
    const aDate =
      a.__typename === 'CaraWorkspaceMessage'
        ? new Date(a.timestamp)
        : a.__typename === 'CaraWorkspacePlanStageEvent'
          ? new Date(a.startedAt)
          : a.__typename === 'CaraWorkspacePlanStage'
            ? new Date(a.createdAt)
            : null;
    const bDate =
      b.__typename === 'CaraWorkspaceMessage'
        ? new Date(b.timestamp)
        : b.__typename === 'CaraWorkspacePlanStageEvent'
          ? new Date(b.startedAt)
          : b.__typename === 'CaraWorkspacePlanStage'
            ? new Date(b.createdAt)
            : null;

    return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
  });

  // Group them by role
  const groups: {
    role: CaraWorkspaceMessageRole;
    items: (
      | CaraWorkspaceFieldsFragment['messages'][0]
      | CaraWorkspaceFieldsFragment['planStages'][0]
      | CaraWorkspaceFieldsFragment['planStages'][0]['events'][0]
    )[];
  }[] = [];

  combined.forEach((item) => {
    if (item.__typename === 'CaraWorkspaceMessage') {
      if (groups.at(-1)?.role === item.role) {
        groups.at(-1)?.items.push(item);
      } else {
        groups.push({ role: item.role, items: [item] });
      }
    } else {
      if (groups.at(-1)?.role === CaraWorkspaceMessageRole.Agent) {
        groups.at(-1)?.items.push(item);
      } else {
        groups.push({ role: CaraWorkspaceMessageRole.Agent, items: [item] });
      }
    }
  });

  // Determine UI quirks that need to be fixed.
  // 1. If the workspace is WORKING and there is no IN_PROGRESS thought, we need to show a "thinking" indicator
  // 2. Display artifacts with the result of the latest plan stage that generated it.
  const thinkingIndicator =
    workspace.state === CaraWorkspaceState.Working &&
    !workspace.planStages.at(-1)?.events.some((e) => !e.endedAt);

  // Now render!
  return groups.map((group, i) => (
    <div key={i} className="flex flex-col gap-2">
      <AnimateIn className={clsx('text-xs text-neutral-500 uppercase font-medium')}>
        {group.role === CaraWorkspaceMessageRole.User ? 'You' : 'Cara'}
      </AnimateIn>
      <div className="flex flex-col gap-2">
        {group.items.map((item, j) => {
          if (
            item.__typename === 'CaraWorkspaceMessage' &&
            group.role === CaraWorkspaceMessageRole.User
          ) {
            return <UserMessage key={item.id} content={item.content} attachments={item.files} />;
          }

          if (
            item.__typename === 'CaraWorkspaceMessage' &&
            group.role === CaraWorkspaceMessageRole.Agent
          ) {
            return (
              <ResponseMessage
                htmlDocumentTemplates={htmlDocumentTemplates}
                artifacts={[]}
                content={item.content}
                workspaceId={workspace.id}
              />
            );
          }

          if (item.__typename === 'CaraWorkspacePlanStage') {
            if (item.type === CaraWorkspacePlanStageType.ProcessAttachments) {
              const title =
                item.state === CaraWorkspacePlanStageState.InProgress
                  ? `Processing attachments...`
                  : item.state === CaraWorkspacePlanStageState.Failed
                    ? 'Failed to process attachments'
                    : 'Processed attachments';

              const content =
                [...(item.processAttachmentsDetails?.progress || [])]
                  .sort((a, b) => a.file.name.localeCompare(b.file.name))
                  .map((p) => `**${p.file.name}**: (${Math.round(p.progress * 1000) / 10}%)`)
                  .join('\n\n') || '';

              return (
                <PlanStageThought
                  title={title}
                  content={content}
                  completed={item.state === CaraWorkspacePlanStageState.Success}
                  error={item.state === CaraWorkspacePlanStageState.Failed}
                />
              );
            }
          }

          if (item.__typename === 'CaraWorkspacePlanStageEvent') {
            return (
              <React.Fragment key={item.id}>
                <StageEvent
                  workspaceId={workspace.id}
                  htmlDocumentTemplates={htmlDocumentTemplates}
                  overrideOpen={j === group.items.length - 1 && i === groups.length - 1}
                  event={item}
                />
                {thinkingIndicator && i === groups.length - 1 && j === group.items.length - 1 && (
                  <PlanStageThought title="Thinking..." content="" completed={false} />
                )}
              </React.Fragment>
            );
          }

          return null;
        })}
      </div>
    </div>
  ));
};

const renderAddress = (address: Address) => {
  if (!address.line1) {
    return '';
  }
  return `${address.line1}, ${address.city}, ${address.zone}, ${address.postalCode}`;
};

const StageEvent = (props: {
  workspaceId: string;
  overrideOpen: boolean;
  event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
}) => {
  if (props.event.type === CaraWorkspacePlanStageEventType.Thought) {
    return (
      <PlanStageThought
        title={props.event.title || 'Thinking...'}
        content={props.event.content || ''}
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.SearchFile) {
    return (
      <PlanStageThought
        title="Searching files"
        content={
          props.event.toolCall?.searchFileTool?.output?.results?.length
            ? props.event.toolCall?.searchFileTool?.output?.results
                .map((r, i) => `# ${i + 1}. ${r.query}\n\n${r.answer}`)
                .join('\n\n')
            : props.event.toolCall?.searchFileTool?.input?.queries?.length
              ? props.event.toolCall?.searchFileTool?.input?.queries
                  .map((q, i) => `# ${i + 1}. ${q.query}`)
                  .join('\n\n')
              : ''
        }
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.SearchKnowledgeBase) {
    return (
      <PlanStageThought
        title="Searching knowledge base"
        content={
          props.event.toolCall?.searchKnowledgeBaseTool?.output?.documents?.length
            ? props.event.toolCall?.searchKnowledgeBaseTool?.output?.documents
                .map(
                  (d, i) => `# ${i + 1}. ${d.metadata.title} (${d.relevantPages.length} pages)\n\n`
                )
                .join('\n\n')
            : props.event.endedAt
              ? 'No relevant results found in the knowledge base'
              : props.event.toolCall?.searchKnowledgeBaseTool?.input?.queries?.length
                ? props.event.toolCall?.searchKnowledgeBaseTool?.input?.queries
                    .map((q, i) => `# ${i + 1}. ${q.query}`)
                    .join('\n\n')
                : ''
        }
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.SearchKnowledgeGraph) {
    return (
      <PlanStageThought
        title="Searching knowledge graph"
        content={
          props.event.toolCall?.searchKnowledgeGraphTool?.output?.entities?.length
            ? `Found ${props.event.toolCall.searchKnowledgeGraphTool.output.entities.length} entit${props.event.toolCall.searchKnowledgeGraphTool.output.entities.length === 1 ? 'y' : 'ies'}`
            : props.event.endedAt
              ? 'No relevant results found in the knowledge graph'
              : props.event.toolCall?.searchKnowledgeGraphTool?.input?.queries?.length
                ? props.event.toolCall?.searchKnowledgeGraphTool?.input?.queries
                    .map((q, i) => `# ${i + 1}. ${q.query}`)
                    .join('\n\n')
                : ''
        }
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.SearchWeb) {
    return (
      <PlanStageThought
        title="Searching the web"
        content={
          props.event.toolCall?.searchWebTool?.output?.results?.length
            ? props.event.toolCall?.searchWebTool?.output?.results
                .map((r, i) => `# ${i + 1}. ${r.query}\n\n${r.answer}`)
                .join('\n\n')
            : props.event.toolCall?.searchWebTool?.input?.queries?.length
              ? props.event.toolCall?.searchWebTool?.input?.queries
                  .map((q, i) => `# ${i + 1}. ${q.query}`)
                  .join('\n\n')
              : ''
        }
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.WriteIntegration) {
    const content = props.event.error
      ? props.event.error
      : props.event.toolCall?.writeIntegrationTool?.output?.success === false
        ? props.event.toolCall?.writeIntegrationTool?.output?.message
        : props.event.toolCall?.writeIntegrationTool?.input?.action
          ? [
              {
                key: 'Action',
                value: titleCase(props.event.toolCall!.writeIntegrationTool!.input.action)
              },
              ...Object.entries(props.event.toolCall!.writeIntegrationTool!.input.account).map(
                ([key, value]) =>
                  key === 'address' && typeof value === 'object'
                    ? {
                        key: 'Address',
                        value: renderAddress(value)
                      }
                    : { key: titleCaseFromCamelCase(key), value }
              ),
              ...Object.entries(
                props.event.toolCall!.writeIntegrationTool!.input.uploadDocument || {}
              ).map(([key, value]) => ({ key: titleCaseFromCamelCase(key), value })),
              ...Object.entries(
                props.event.toolCall!.writeIntegrationTool!.input.createNote || {}
              ).map(([key, value]) => ({ key: titleCaseFromCamelCase(key), value })),
              ...Object.entries(
                props.event.toolCall!.writeIntegrationTool!.input.createTask || {}
              ).map(([key, value]) => ({ key: titleCaseFromCamelCase(key), value }))
            ]
              .filter((p) => !!p.value && !p.key.startsWith('__'))
              .map((p) => `**${p.key}**: ${p.value}`)
              .join('\n\n')
          : '';

    return (
      <PlanStageThought
        title="Pushing data via integration"
        content={content}
        completed={!!props.event.endedAt}
        overrideOpen={props.overrideOpen}
        error={props.event.error}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.DraftEmail && !props.event.error) {
    if (!props.event.toolCall?.draftEmailTool?.input) {
      return <PlanStageThought title="Drafting email..." content="" completed={false} />;
    }

    return (
      <EmailDraftArtifact
        email={props.event.toolCall?.draftEmailTool?.input}
        completed={!!props.event.endedAt}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.DraftDocument && !props.event.error) {
    if (!props.event.toolCall?.draftDocumentTool?.input) {
      return <PlanStageThought title="Drafting document..." content="" completed={false} />;
    }

    return (
      <DocumentDraftArtifact
        workspaceId={props.workspaceId}
        document={props.event.toolCall?.draftDocumentTool?.input}
        htmlDocumentTemplates={props.htmlDocumentTemplates}
        completed={!!props.event.endedAt}
      />
    );
  }

  if (props.event.toolCall?.type === CaraWorkspaceTool.FillTemplate && !props.event.error) {
    if (!props.event.toolCall?.fillTemplateTool?.input) {
      return <PlanStageThought title="Filling template..." content="" completed={false} />;
    }

    return (
      <FillTemplateArtifact
        workspaceId={props.workspaceId}
        toolCall={props.event.toolCall?.fillTemplateTool}
        completed={!!props.event.endedAt}
      />
    );
  }

  return null;
};

const EmailDraftArtifact = (props: { email: DraftEmailToolInput; completed: boolean }) => {
  const [copied, setCopied] = React.useState(false);
  const [emailBody, setEmailBody] = React.useState('');

  React.useEffect(() => {
    const fetchEmailBody = async () => {
      if (props.email.body) {
        const text = await remark()
          .use(stripMarkdown, {
            keep: ['list', 'listItem']
          })
          .process(props.email.body);
        setEmailBody(text.toString());
      }
    };

    fetchEmailBody();
  }, [props.email.body]);

  const copyText = async (input: string) => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(input);
    navigator.clipboard.writeText(text.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openEmailClient = async () => {
    const text = await remark()
      .use(stripMarkdown, {
        keep: ['list', 'listItem']
      })
      .process(props.email.body || '');

    window.open(
      `mailto:${encodeURIComponent(props.email.toEmail || '')}?subject=${encodeURIComponent(
        props.email.subject || ''
      )}&body=${encodeURIComponent(text.toString())}`,
      '_blank'
    );
  };

  return (
    <AnimateIn
      shimmer={!props.completed}
      className="rounded-lg shadow bg-white p-4 max-w-xl relative"
    >
      <div className="absolute top-3 right-3 flex flex-row gap-2">
        <Headless.Menu as="div" className="relative">
          <Headless.MenuButton className="inline-flex items-center rounded-full p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-150">
            <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
          </Headless.MenuButton>

          <Headless.MenuItems
            transition
            className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <Headless.MenuItem>
              <div
                className="flex flex-row items-center gap-2 px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                onClick={() => copyText(props.email.body || '')}
              >
                {copied ? (
                  <CheckIcon className="size-4 text-neutral-500" />
                ) : (
                  <ClipboardDocumentIcon className="size-4 text-neutral-500" />
                )}
                Copy as plain text
              </div>
            </Headless.MenuItem>
            <Headless.MenuItem>
              <div
                className="flex flex-row items-center gap-2 px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                onClick={() => openEmailClient()}
              >
                <EnvelopeIcon className="size-4 text-neutral-500" />
                Open in email client
              </div>
            </Headless.MenuItem>
          </Headless.MenuItems>
        </Headless.Menu>
      </div>
      {!props.completed && (
        <Badge color="blue" className="mb-2">
          Drafting email
        </Badge>
      )}
      <p className="text-sm font-medium text-neutral-700 pr-20">{props.email.subject}</p>
      <p className="text-sm text-neutral-500 pr-20">{props.email.toEmail}</p>
      <pre className="text-sm font-body mt-4 text-neutral-700 whitespace-break-spaces">
        {emailBody}
      </pre>
    </AnimateIn>
  );
};

const DocumentDraftArtifact = (props: {
  workspaceId: string;
  document: DraftDocumentToolInput;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  completed: boolean;
}) => {
  const [showHtmlDocument, setShowHtmlDocument] = React.useState(false);
  const [selectedStyleId, setSelectedStyleId] = React.useState<string>('');
  return (
    <>
      <Dialog open={showHtmlDocument} onClose={() => setShowHtmlDocument(false)} size="4xl">
        <DialogTitle>{props.document.title}</DialogTitle>
        <DialogBody>
          <Select
            value={selectedStyleId}
            onChange={(e) => setSelectedStyleId(e.target.value)}
            className="max-w-80"
          >
            <option value="">None</option>
            <optgroup label="Built-in styles">
              <option value="builtin:classic-professional">Classic Professional</option>
              <option value="builtin:modern-minimalist">Modern Minimalist</option>
              <option value="builtin:warm-human">Warm Human</option>
              <option value="builtin:elegant-premium">Elegant Premium</option>
              <option value="builtin:technical-structured">Technical and Structured</option>
            </optgroup>
            <optgroup label="Custom styles">
              {props.htmlDocumentTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
              {props.htmlDocumentTemplates.length === 0 && (
                <option disabled>No custom styles found</option>
              )}
            </optgroup>
          </Select>
          <iframe
            src={`${getStaticsBaseUrl()}/agency/html/${props.workspaceId}/${props.document.artifactId}?style_id=${selectedStyleId}`}
            className="h-[calc(100vh-16rem)] w-full mt-4 rounded-lg bg-neutral-50 border border-neutral-100"
            title={props.document.title}
          />
        </DialogBody>
      </Dialog>
      <AnimateIn
        shimmer={!props.completed}
        className="rounded-lg shadow bg-white p-4 max-w-xl relative"
      >
        {!props.completed && (
          <AnimateIn className="relative">
            <Badge color="blue" className="mb-4">
              Drafting document
            </Badge>
          </AnimateIn>
        )}
        <div className="flex justify-between gap-4">
          <div className="flex flex-row gap-2">
            <PaperClipIcon className="size-6 text-neutral-500" />
            <div>
              <p className="text-sm font-medium text-neutral-700">{props.document.title}</p>
            </div>
          </div>
          <Button
            plain
            target="_blank"
            onClick={() => setShowHtmlDocument(true)}
            className="flex-none"
          >
            Preview Document
          </Button>
        </div>
      </AnimateIn>
    </>
  );
};

const FillTemplateArtifact = (props: {
  workspaceId: string;
  toolCall: FillTemplateToolFieldsFragment;
  completed: boolean;
}) => {
  return (
    <>
      <AnimateIn
        shimmer={!props.completed}
        className="rounded-lg shadow bg-white p-4 max-w-xl relative"
      >
        {!props.completed && (
          <AnimateIn className="relative">
            <Badge color="blue" className="mb-4">
              Filling template
            </Badge>
          </AnimateIn>
        )}
        <div className="flex justify-between gap-4">
          <div className="flex flex-row gap-2">
            <PaperClipIcon className="size-6 text-neutral-500" />
            <div>
              <p className="text-sm font-medium text-neutral-700">
                {props.toolCall.input.templateId.replace(/_/g, ' ')}
              </p>
              <p className="text-sm text-neutral-500">
                {props.toolCall.input.acord24?.certificateHolder?.fullName ||
                  props.toolCall.input.acord24?.namedInsured?.fullName ||
                  props.toolCall.input.acord25?.holder.name ||
                  props.toolCall.input.acord25?.insured.name ||
                  props.toolCall.input.acord27?.additionalInterest?.fullName ||
                  props.toolCall.input.acord27?.namedInsured?.fullName ||
                  props.toolCall.input.acord28?.additionalInterest?.fullName ||
                  props.toolCall.input.acord28?.insured?.fullName ||
                  props.toolCall.input.acord35?.namedInsured?.fullName ||
                  props.toolCall.input.acord36?.agencies.newAgency?.fullName ||
                  props.toolCall.input.acord75?.namedInsured?.fullName ||
                  props.toolCall.input.acord125?.applicant1.name ||
                  props.toolCall.input.acord126?.namedInsured ||
                  props.toolCall.input.acord130?.namedInsured?.fullName ||
                  props.toolCall.input.acord131?.namedInsured ||
                  props.toolCall.input.acord140?.namedInsured ||
                  ''}
              </p>
            </div>
          </div>
          <Button
            plain
            target="_blank"
            className={clsx(!props.completed && 'opacity-50')}
            href={`${getStaticsBaseUrl()}/agency/template/${props.workspaceId}/${props.toolCall.output.artifactId}`}
          >
            View Document
          </Button>
        </div>
      </AnimateIn>
    </>
  );
};
